import { Loader } from "@googlemaps/js-api-loader"
import flatpickr from "flatpickr"
import { DateTime } from "luxon"
import * as util from "./util"

/* To locally debug this script via Webflow the best option I've found is to
add two scripts in Webflow:
<script src="http://localhost:3000/????.bundle.js"></script>
<script src="angry-boyd-1669aa.netlify.app/????.bundle.js" crossorigin="anonymous"></script>
The below check ensures that if localhost loads successfully the second
(production) script won't run.
Initially I tried to add the script dynamically in Webflow but that didn't
work - I needed it to run immediately but it wouldn't. */
if (typeof SharesyWebflow === "undefined") {
  window.JetboostListSearchComplete = function (listWrapperNode, totalResults) {
    SharesyWebflow.jetboostFilterComplete(listWrapperNode)
  }
  window.JetboostPaginationComplete = function (collectionListWrapperElement) {
    SharesyWebflow.jetboostPaginationComplete(collectionListWrapperElement)
  }

  Promise.deferred = util.promiseDeferred

  util.initSentry()

  window.amplitudeOnLoad = function (instance) {
    util.amplitudeInit(instance, "Search Page Viewed", {
      "Search Version": 1,
      "Search AB Test": util.getCookie("search_ab_test"),
    })
  }

  window.tfQuoteQuestionChanged = function (data) {
    SharesyWebflow.logAmplitudeEvent("Search Quote Typeform Question Change", {
      question: data.ref,
    })
  }
  window.tfQuoteReady = function () {
    SharesyWebflow.logAmplitudeEvent("Search Quote Typeform Ready")
  }
  window.tfQuoteSubmit = function () {
    SharesyWebflow.logAmplitudeEvent("Search Quote Typeform Submit")
  }
  window.tfQuoteClose = function () {
    SharesyWebflow.logAmplitudeEvent("Search Quote Typeform Close")
  }

  // Wrap everything with Revealing Module Pattern
  window.SharesyWebflow = (function () {
    var searchParams = util.getSearchParams()

    // By default sort by popularity
    var globalSortBySelector = ".ranking-hidden"
    var globalSortByAttribute = undefined
    var globalSortDescending = true

    var fp
    var date
    var startTime
    var endTime

    var dateTextLong

    // This will store a memoized map of cached space availability data, with
    // date as key
    var spaceAvailabilityMap = {}
    // Save space data to avoid repeated innerText calls
    var spaceData = []
    // A memoized map of SEO content data with area as key
    var contentSectionsMap = {}

    // used to store the result of the call to set-prices
    var prices

    // Time filter status
    const TIME_NONE = 0
    const TIME_START = 1
    const TIME_END = 2
    var timeStatus = TIME_NONE

    var sharesyGmap
    var sharesyGmapDivInfoWindow

    var gmapSelectedPlace
    var gmapLat
    var gmapLng
    var gmapPostcode
    var gmapMarker
    var gmapSavedBounds
    // Some actions change the bounds automatically - we don't want to show
    // "Map area" as the location until they are finished.
    var gmapDontSetTextToMapArea = true
    // Have the markers been added at least once? (i.e. via jetboost update or
    // time filter)
    var gmapRefreshMarkersRunOnce = false
    var gmapDivMarkers = []
    // Used to highlight map icon onhover
    var gmapDivMarkerDict = {}
    // Use for Amplitude logging
    var gmapSpaceDict = {}
    var boundsChangeTimeout

    // Help text displays when user changes map area then switches back to
    // List View
    var showLocationPopupCount = 0
    var mapAreaChanged = false

    // Location text to display in the "remove filters option"
    var removeFiltersLocation = "London"

    // Don't display the number of spaces until pagination is complete
    var readyToShowNumSpaces = false

    const promiseAvail = Promise.deferred()
    const promisePagination = Promise.deferred()
    const promiseMap = Promise.deferred()
    var promisePaginationTimeout = false

    // Original SEO values
    var defaultPageTitle
    var defaultMetaDesc
    var defaultH1
    var defaultMainH2
    var defaultMainH2Body
    var defaultLocation
    var typeItems = []
    var contentSectionItems = []

    var shortSummariesUnhidden = false
    var unhideSpaceShortSummariesRunOnce = false

    var jetboostPaginationUnhideRunOnce = false
    var jetboostPaginationUnhideComplete = false
    var runningUnhideJetboostPages = false
    var runningJetboostFilterComplete = false

    function logAmplitudeEvent(eventName, eventProps) {
      const sp = util.getSearchParams()
      let eventProperties = {}
      if (eventProps) {
        eventProperties = eventProps
      }
      try {
        eventProperties["Selected Date"] =
          document.querySelector(".text-date-input").value
        eventProperties["Start Time"] = startTime
        eventProperties["End Time"] = endTime
        if (!eventProperties["Type"]) {
          eventProperties["Type"] = sp.get("types")
        }
        eventProperties["Location"] = sp.get("loc")
        eventProperties["Area"] = getArea(sp)
        eventProperties["Search Version"] = 1
        eventProperties["Search AB Test"] = util.getCookie("search_ab_test")
      } catch (err) {}
      util.logAmplitudeEvent(eventName, eventProperties)
    }

    // If current scroll position is near the bottom the user may not realise a change
    // they've just made has had an effect, so scroll them to the top.
    function scrollToTopIfNeeded() {
      let resultsVisible = true
      try {
        if (
          document.getElementById("results-column").style.display === "none"
        ) {
          resultsVisible = false
        }
      } catch (err) {}
      if (resultsVisible) {
        let bodyTop = document.body.getBoundingClientRect().top
        let breadcrumbTop = document
          .getElementsByClassName("breadcrumbs")[0]
          .getBoundingClientRect().top
        let offset = breadcrumbTop - bodyTop
        if (offset - scrollY < 1800 || bodyTop < -250) {
          window.scrollTo(0, 0)
        }
      }
    }

    async function gmapMainRun() {
      // Handle location selected via autocomplete
      let locationSearch = document.getElementById("location-search")
      const options = {
        componentRestrictions: { country: "uk" },
        types: ["geocode"],
      }
      try {
        let autocomplete = new google.maps.places.Autocomplete(
          locationSearch,
          options
        )
        google.maps.event.addListener(
          autocomplete,
          "place_changed",
          function () {
            let place = autocomplete.getPlace()
            gmapSelectedPlace = place.place_id
            if (gmapSelectedPlace) {
              gmapMakeRequest()
              const eventProperties = {
                "Place Name": place.name,
                Latitude: place.geometry.location.lat(),
                Longitude: place.geometry.location.lng(),
              }
              scrollToTopIfNeeded()
              autoOpenCalendar(1000)
              logAmplitudeEvent("Select Autocomplete Location", eventProperties)
            }
          }
        )
      } catch (err) {
        logAmplitudeEvent(err)
      }
      try {
        // Prevent form submission if the user presses Enter within the input
        locationSearch.addEventListener("keydown", function (event) {
          if (event.code === "Enter") {
            event.preventDefault()
          }
        })
      } catch (err) {
        logAmplitudeEvent(err)
      }

      await gmapDisplayMap()
      promiseMap.then(() => {
        updatesAfterMapAndAvail()
      })
    }

    function unhideSpaceShortSummaries() {
      if (!searchParams.has("s")) {
        const listitems = Array.from(
          document.getElementsByClassName("spaces-list-item")
        )
        for (const l of listitems) {
          if (
            !l.classList.contains("area-hide") &&
            !l.classList.contains("map-hide") &&
            !l.classList.contains("hide") &&
            !l.classList.contains("hide-enquiry-only") &&
            !l.classList.contains("jetboost-list-item-hide")
          ) {
            l.querySelector(".space-desc").classList.add("unhide")
          }
        }
        /* When the page loads, Jetboost calls jetboostFilterComplete once per
        filter. That calls updateSpaceShortSummaries each time. To prevent those
        initial calls from removing the short summaries we set a timeout,
        assuming user actions won't happen within the first 4 seconds. */
        setTimeout(function () {
          shortSummariesUnhidden = true
        }, 4000)
        unhideSpaceShortSummariesRunOnce = true
      }
    }

    // Because the short summaries are only there as SEO content, we hide them when the user interacts with the page.
    function updateSpaceShortSummaries() {
      if (shortSummariesUnhidden) {
        const spaceDescs = Array.from(
          document.getElementsByClassName("space-desc")
        )
        for (const l of spaceDescs) {
          l.classList.remove("unhide")
        }
        // Run again, to unhide summaries for spaces that were initially hidden when unhideSpaceShortSummaries first ran
      } else if (unhideSpaceShortSummariesRunOnce) {
        unhideSpaceShortSummaries()
      }
    }

    function updatesAfterMapAndAvail() {
      // Wait until availability has loaded
      promiseAvail.then(() => {
        // Once we have a map and pricing data has been returned we can add the
        // markers. Also need to rerun operations that filter.
        if (searchParams.get("loc")) {
          gmapRefreshMarkers(true)
          processLocationQueryParam()
        } else {
          setArea(getArea(util.getSearchParams()), true)
          gmapRefreshMarkers(false)
        }
        let showCalendar = true
        // If pressing back to map view, show the map
        if (searchParams.has("view")) {
          if (searchParams.get("view") === "map") {
            selectShowMap(false)
            showCalendar = false
          }
        }
        // Wait for map to load (gmapMainRun->gmapDisplayMap). If map=1 then the
        // H1 is moved down so the filter buttons move up. If we don't wait the
        // open calendar can be left floating over the map.
        if (showCalendar) {
          autoOpenCalendar(1000)
        }
      })
    }

    async function processLocationQueryParam() {
      let loc = searchParams.get("loc")
      if (loc) {
        let gmapAutocompleteService =
          new google.maps.places.AutocompleteService()
        gmapAutocompleteService
          .getPlacePredictions(
            {
              input: loc,
              componentRestrictions: { country: "uk" },
              sessionToken: new google.maps.places.AutocompleteSessionToken(),
            },
            gmapDisplaySuggestions
          )
          .then(() => gmapMakeRequest())
      }
    }

    const gmapDisplaySuggestions = function (predictions, status) {
      if (
        status !== google.maps.places.PlacesServiceStatus.OK ||
        !predictions
      ) {
        return
      }
      gmapSelectedPlace = predictions[0].place_id
    }

    async function gmapMakeRequest() {
      if (gmapSelectedPlace) {
        let gmapRequest = {
          placeId: gmapSelectedPlace,
          fields: ["geometry", "name"],
        }
        let service = new google.maps.places.PlacesService(
          document.createElement("div")
        )
        await service.getDetails(gmapRequest, gmapGetDetailsCallback)
      }
    }

    async function gmapGetDetailsCallback(place, status) {
      if (status === google.maps.places.PlacesServiceStatus.OK) {
        gmapLat = place.geometry.location.lat()
        gmapLng = place.geometry.location.lng()
        gmapPostcode = place.name
      }
      util.updateUrlParam("loc", place.name, false, false)
      util.deleteUrlParam("area")
      setUserLocation(place.name)
      seoUpdates()
    }

    async function gmapDisplayDistancesFromUser() {
      let from = new google.maps.LatLng(gmapLat, gmapLng)
      for (const space of spaceData) {
        let to = new google.maps.LatLng(space.lat, space.lng)
        let dist = await google.maps.geometry.spherical.computeDistanceBetween(
          from,
          to
        )
        let distanceInMiles = (dist / 1609.344).toFixed(1)

        let addressVenue = space.item.querySelector(".address-venue")
        if (!space.venueAddress || !space.venueAddressTruncated) {
          space.venueAddress = addressVenue.innerText
          // Set a truncated address e.g. "Chalgrove Gardens, N3"
          space.venueAddressTruncated = util.removeRightPartOfPostcode(
            space.item.querySelector(".address-hidden").innerText
          )
        }
        addressVenue.innerText =
          distanceInMiles +
          " mi from " +
          gmapPostcode +
          " (" +
          space.venueAddressTruncated +
          ")"
        addressVenue.dataset.distanceInMiles = distanceInMiles

        let addressSpace = space.item.querySelector(".address-space")
        if (!space.spaceAddress || !space.spaceAddressTruncated) {
          space.spaceAddress = addressSpace.innerText
          space.spaceAddressTruncated = util.removeRightPartOfPostcode(
            space.item.querySelector(".address-space-hidden").innerText
          )
        }
        addressSpace.innerText =
          distanceInMiles +
          " mi from " +
          gmapPostcode +
          " (" +
          space.spaceAddressTruncated +
          ")"
        addressSpace.dataset.distanceInMiles = distanceInMiles
      }
      document.getElementById("distance-low-to-high").style.display = "block"
      sort("distance-low-to-high")
      closeModals()
    }

    async function gmapDisplayMap() {
      try {
        let pageWidth =
          window.innerWidth ||
          document.documentElement.clientWidth ||
          document.body.clientWidth
        if (pageWidth >= util.TABLET_MAX_WIDTH) {
          let h1 = document.getElementById("h1-title")
          document.getElementById("sort-row").prepend(h1)
          document
            .getElementById("search-header-container")
            .classList.remove("mobile")
        }
        let containerFooter = document.getElementById("container-footer")
        containerFooter.style.paddingLeft = "20px"
        containerFooter.style.paddingRight = "20px"
      } catch (err) {
        util.logToSentry(err)
      }

      const TOP = 1
      const BOTTOM = 2
      const CENTRE = 1
      const LEFT = 2
      const RIGHT = 3

      DivInfoWindow.prototype = new google.maps.OverlayView()
      DivInfoWindow.prototype.draw = function () {
        let me = this
        let div = this.div_
        let map = this.map_
        if (!div) {
          // Create a overlay text DIV
          div = this.div_ = document.createElement("div")
          div.style.position = "absolute"
          div.style.cursor = "default"
          div.style.zIndex = 100

          this.div_.contentDiv = document.createElement("div")
          this.div_.contentDiv.innerHTML = this.content_
          this.div_.appendChild(this.div_.contentDiv)

          this.div_.contentDiv.style.padding = "10px"
          this.div_.contentDiv.style.zIndex = 1
          this.div_.contentDiv.style.width = "250px"
          this.div_.contentDiv.style.height = "230px"
          this.div_.contentDiv.style.border = "none"
          this.div_.contentDiv.style.backgroundColor = "white"
          this.div_.contentDiv.style.borderRadius = "12px"
          this.div_.contentDiv.style.boxShadow =
            "rgb(0 0 0 / 4%) 0px 0px 0px 1px, rgb(0 0 0 / 18%) 0px 2px 4px"
          this.div_.contentDiv.style.fontFamily = "objektiv-mk1,sans-serif"
          this.div_.contentDiv.style.fontSize = "14px"
          this.div_.contentDiv.style.overflowY = "auto"
          this.div_.contentDiv.style.overflowX = "hidden"

          const spaces =
            this.div_.contentDiv.getElementsByClassName("map-info-space")
          for (const space of spaces) {
            space.addEventListener("click", function () {
              const eventProps = gmapSpaceDict[space.dataset.slug]
              logAmplitudeEvent("Select Space", eventProps)
            })
          }

          div.addEventListener("click", function (event) {
            event.stopPropagation()
          })
          div.addEventListener("touchstart", function (event) {
            event.stopPropagation()
          })
          div.addEventListener("touchend", function (event) {
            event.stopPropagation()
          })
          div.addEventListener("touchmove", function (event) {
            event.stopPropagation()
          })

          // Don't zoom the map when user tries to scroll within InfoWindow.
          // Also add for touchstart?
          div.addEventListener("mouseover", function () {
            map.setOptions({
              disableDoubleClickZoom: true,
              navigationControl: false,
              scaleControl: false,
              draggable: false,
            })
          })
          // Restore control to page body and google map
          // when user is no longer hovering over InfoWindow.
          // Also add for touchend and touchcancel?
          div.addEventListener("mouseout", function () {
            map.setOptions({
              disableDoubleClickZoom: false,
              navigationControl: true,
              scaleControl: true,
              draggable: true,
            })
          })
          // Then add the overlay to the DOM
          let panes = this.getPanes()
          panes.overlayImage.appendChild(div)
        }

        let point = this.getProjection().fromLatLngToDivPixel(this.latlng_)
        if (point) {
          if (this.displayVertical_ === TOP) {
            div.style.top = point.y - 260 + "px"
          } else {
            div.style.top = point.y + 30 + "px"
          }
          if (this.displayHorizontal_ === CENTRE) {
            div.style.left = point.x - 125 + "px"
          } else if (this.displayHorizontal_ === LEFT) {
            div.style.left = point.x - 250 + "px"
          } else {
            div.style.left = point.x - 0 + "px"
          }
        }
      }

      DivInfoWindow.prototype.remove = function () {
        // Check if the overlay was on the map and needs to be removed.
        if (this.div_) {
          this.div_.parentNode.removeChild(this.div_)
          this.div_ = null
        }
      }

      DivInfoWindow.prototype.getPosition = function () {
        return this.latlng_
      }

      DivInfoWindow.prototype.setContent = function (content) {
        this.content_ = content
      }

      DivInfoWindow.prototype.open = function (map, latLng) {
        //if (anchor) this.position_ = anchor.getPosition();
        this.latlng_ = latLng
        this.setMap(map)
        this.map_ = map

        let scale = Math.pow(2, map.getZoom())
        let nw = new google.maps.LatLng(
          map.getBounds().getNorthEast().lat(),
          map.getBounds().getSouthWest().lng()
        )
        let worldCoordinateNW = map.getProjection().fromLatLngToPoint(nw)
        let worldCoordinate = map.getProjection().fromLatLngToPoint(latLng)
        let pixelOffset = new google.maps.Point(
          Math.floor((worldCoordinate.x - worldCoordinateNW.x) * scale),
          Math.floor((worldCoordinate.y - worldCoordinateNW.y) * scale)
        )
        let width = map.getDiv().offsetWidth
        let height = map.getDiv().offsetHeight
        this.displayVertical_ = TOP
        this.displayHorizontal_ = CENTRE
        if (pixelOffset.x < 127) {
          this.displayHorizontal_ = RIGHT
        } else if (width - pixelOffset.x < 127) {
          this.displayHorizontal_ = LEFT
        }
        if (pixelOffset.y < 305) {
          this.displayVertical_ = BOTTOM
        }
      }

      // Remove the InfoWindow from the map.
      DivInfoWindow.prototype.close = function () {
        this.setMap(null)
      }

      function DivInfoWindow() {}

      let north = 54.53324237
      let south = 50.78680908
      let west = -3.0337857
      let east = 0.44249003
      try {
        let northBounds = document.querySelector(
          ".bounds-north-hidden"
        ).innerText
        if (northBounds) {
          north = parseFloat(northBounds)
          let southBounds = document.querySelector(
            ".bounds-south-hidden"
          ).innerText
          south = parseFloat(southBounds)
          let westBounds = document.querySelector(
            ".bounds-west-hidden"
          ).innerText
          west = parseFloat(westBounds)
          let eastBounds = document.querySelector(
            ".bounds-east-hidden"
          ).innerText
          east = parseFloat(eastBounds)
        }
      } catch (err) {
        util.logToSentry(err)
      }
      const OUTER_MAP_BOUNDS = {
        north: north + 0.1,
        south: south - 0.1,
        west: west - 0.07,
        east: east + 0.07,
      }
      sharesyGmap = new google.maps.Map(
        document.getElementById("map-container"),
        {
          center: { lat: 51.51509677, lng: -0.15 }, // London
          zoom: 11,
          options: {
            gestureHandling: "greedy",
          },
          zoomControl: true,
          zoomControlOptions: {
            position: google.maps.ControlPosition.TOP_RIGHT,
          },
          clickableIcons: false,
          fullscreenControl: false,
          streetViewControl: false,
          mapTypeControl: false,
          mapId: "5864843f565cbca8",
          restriction: {
            latLngBounds: OUTER_MAP_BOUNDS,
            strictBounds: false,
          },
        }
      )
      google.maps.event.addListenerOnce(
        sharesyGmap,
        "tilesloaded",
        function () {
          // Until this runs e.g. map.getBounds is undefined
          promiseMap.resolve()
        }
      )

      sharesyGmap.addListener("click", function (event) {
        sharesyGmapDivInfoWindow.close()
      })
      sharesyGmap.addListener("dragend", gmapBoundsChange)
      sharesyGmap.addListener("zoom_changed", gmapBoundsChange)

      sharesyGmapDivInfoWindow = new DivInfoWindow()
    }

    async function gmapBoundsChange() {
      function finished() {
        let bounds = sharesyGmap.getBounds()
        let boundsChanged = true
        try {
          boundsChanged = !gmapSavedBounds.equals(bounds)
        } catch (err) {}
        if (boundsChanged && sharesyGmap.userBoundsChange) {
          blurSpaces()
          let ne = bounds.getNorthEast()
          let sw = bounds.getSouthWest()
          let boundsNorth = ne.lat()
          let boundsSouth = sw.lat()
          let boundsWest = sw.lng()
          let boundsEast = ne.lng()
          for (const space of spaceData) {
            if (
              isNaN(space.lat) ||
              isNaN(space.lng) ||
              space.lat > boundsNorth ||
              space.lat < boundsSouth ||
              space.lng < boundsWest ||
              space.lng > boundsEast
            ) {
              space.item.classList.add("map-hide")
            } else {
              space.item.classList.remove("map-hide")
            }
            space.item.classList.remove("area-hide")
          }
          gmapSavedBounds = bounds
          // Don't show "Map area" if the bounds have changed as a result of a programmatic change (e.g. selecting "Finchley").
          if (!gmapDontSetTextToMapArea) {
            document.getElementById("location-button-text").innerText =
              "Map area"
            mapAreaChanged = true
            removeFiltersLocation = "the area shown on the map"
            logAmplitudeEvent("Change Map Area")
            unhideJetboostPages(20)
          }
          refreshNumSpaces()
          refreshRemoveFilters()
          updateSpaceShortSummaries()
        }
        sharesyGmap.userBoundsChange = true
      }
      clearTimeout(boundsChangeTimeout)
      boundsChangeTimeout = setTimeout(finished, 200)
    }

    // If redrawBounds is set then reset the map bounds based on all the currently unfiltered venues.
    async function gmapRefreshMarkers(redrawBounds = false) {
      if (typeof google === "undefined") {
        return
      }
      DivMarker.prototype = new google.maps.OverlayView()
      DivMarker.prototype.draw = function () {
        let me = this
        let div = this.div_
        let venueVisible = this.venueVisible_
        let venueName = this.venueName_
        let selected = this.selected_
        if (!div) {
          // Create a overlay text DIV
          div = this.div_ = document.createElement("div")
          div.style.border = "none"
          div.style.position = "absolute"
          div.style.paddingLeft = "0px"
          div.style.cursor = "pointer"

          let span = document.createElement("span")
          if (venueVisible) {
            span.style.color = "#000000"
          } else {
            span.style.color = "#C9C0C3"
          }
          span.style.fontWeight = "600"
          span.style.fontFamily = "objektiv-mk1,sans-serif"
          span.style.fontSize = "14px"
          span.style.paddingLeft = "8px"
          span.style.paddingRight = "8px"
          span.style.paddingTop = "4px"
          span.style.paddingBottom = "4px"
          span.style.position = "absolute"
          span.style.minWidth = "40px"
          if (selected) {
            span.style.backgroundColor = "#efefef"
          } else {
            span.style.backgroundColor = "white"
          }
          span.style.borderRadius = "15px"
          span.style.boxShadow =
            "rgb(0 0 0 / 4%) 0px 0px 0px 1px, rgb(0 0 0 / 18%) 0px 2px 4px"
          span.style.whiteSpace = "nowrap"
          span.className = "markerOverlay"
          span.appendChild(document.createTextNode(this.value))
          div.appendChild(span)
          div.addEventListener("click", function (event) {
            // stop click reaction to other layers
            event.stopPropagation()
            google.maps.event.trigger(me, "click")
            logAmplitudeEvent("Select Map Marker", { Venue: venueName })
          })

          // Add the overlay to the DOM
          let panes = this.getPanes()
          panes.overlayImage.appendChild(div)
        }
        // Position the overlay (subtract roughly half the height and width to centre)
        let point = this.getProjection().fromLatLngToDivPixel(this.latlng_)
        if (point) {
          div.style.left = point.x - 21 + "px"
          div.style.top = point.y - 13 + "px"
        }
      }

      DivMarker.prototype.remove = function () {
        // Check if the overlay was on the map and needs to be removed.
        if (this.div_) {
          this.div_.parentNode.removeChild(this.div_)
          this.div_ = null
        }
      }

      DivMarker.prototype.getPosition = function () {
        return this.latlng_
      }

      DivMarker.prototype.select = function () {
        if (!this.selected_) {
          this.selected_ = true
          this.div_.querySelector("span").style.backgroundColor = "#efefef"
          this.div_.querySelector("span").style.color = "#000000"
          this.div_.querySelector("span").style.zIndex = "1"
        }
      }

      DivMarker.prototype.highlight = function () {
        this.div_.querySelector("span").style.backgroundColor = "#1aa979"
        this.div_.querySelector("span").style.color = "#ffffff"
        this.div_.querySelector("span").style.zIndex = "2"
      }

      DivMarker.prototype.unhighlight = function () {
        this.div_.querySelector("span").style.backgroundColor = "#ffffff"
        this.div_.querySelector("span").style.color = "#000000"
        this.div_.querySelector("span").style.zIndex = "auto"
        this.selected_ = false
      }

      function DivMarker(latlng, map, value, venueVisible, venueName) {
        this.latlng_ = latlng
        this.value = value
        this.venueVisible_ = venueVisible
        this.venueName_ = venueName
        this.selected_ = false
        // Do this or nothing will happen
        this.setMap(map)
      }

      function addVenueMarker(
        venueName,
        lat,
        lng,
        markerLabel,
        venueVisible,
        venueContent
      ) {
        if (venueVisible && lat && lng) {
          const latLng = new google.maps.LatLng(lat, lng)
          const divMarker = new DivMarker(
            latLng,
            sharesyGmap,
            markerLabel,
            venueVisible,
            venueName
          )
          let index = gmapDivMarkers.length
          gmapDivMarkers.push(divMarker)
          // Use toFixed to avoid floating point errors. Would be better to replace later with venue slug.
          gmapDivMarkerDict[[venueName, lat.toFixed(4), lng.toFixed(4)]] =
            divMarker
          bounds.extend(latLng)
          if (!venueVisible) {
            venueContent.querySelector(".map-info-venue-name").style.opacity =
              "0.3"
          }
          let content = venueContent.outerHTML
          google.maps.event.addListener(divMarker, "click", function (e) {
            gmapDivMarkers[index].select()
            sharesyGmapDivInfoWindow.setContent(content)
            sharesyGmapDivInfoWindow.open(sharesyGmap, latLng)
            sharesyGmapDivInfoWindow.draw()
          })
        }
      }

      // Delete existing markers
      for (let i = 0; i < gmapDivMarkers.length; i++) {
        gmapDivMarkers[i].setMap(null)
        gmapDivMarkers[i].remove()
      }
      gmapDivMarkers = []

      let bounds = new google.maps.LatLngBounds()

      for (let space of spaceData) {
        // Setting the price in updateSpaceData results in blank prices being
        // shown on the map initially, so set it here instead.
        if (!space.price) {
          space.price = space.item.querySelector(".price").innerText
        }
        let visible = true
        if (redrawBounds) {
          // Bounds will change so just unhide everything
          space.item.classList.remove("area-hide")
          space.item.classList.remove("map-hide")
        }
        // Only add markers for spaces currently visible - too many markers
        // looks strange and impacts performance
        if (
          space.item.classList.contains("hide") ||
          space.item.classList.contains("hide-enquiry-only") ||
          space.item.classList.contains("jetboost-list-item-hide")
        ) {
          visible = false
        }
        space.visible = visible
        space.venueVisible = visible
      }

      // Sort by lat/lng and venue name (below there is one marker for each
      // unique combo), then sort spaces by visibility and sort order.
      spaceData.sort((a, b) => {
        if (!a.lat || !a.lng) {
          return 1
        }
        if (!b.lat || !b.lng) {
          return -1
        }
        if (a.lat < b.lat) {
          return -1
        } else if (a.lat > b.lat) {
          return 1
        }
        if (a.lng < b.lng) {
          return -1
        } else if (a.lng > b.lng) {
          return 1
        }
        if (a.venueName !== b.venueName) {
          return a.venueName - b.venueName
        }
        if (a.visible && b.visible) {
          return b.ranking - a.ranking
        } else if (a.visible) {
          return -1
        } else {
          return 1
        }
      })

      // Set venueVisible and venueRanking - rely on previous sort sorting visible spaces first
      let venueKey
      let venueVisible
      let venueRanking
      for (const s of spaceData) {
        const newVenueKey = s.venueName + s.lat + s.lng
        if (venueKey === newVenueKey) {
          s.venueVisible = venueVisible
          s.venueRanking = venueRanking
        } else {
          venueKey = newVenueKey
          venueVisible = s.visible
          venueRanking = s.venueRanking
        }
      }

      // Sort visible venues last so they are drawn on the map last (otherwise hidden by the non-visible venues)
      spaceData.sort((a, b) => {
        if (a.venueVisible && !b.venueVisible) {
          return 1
        }
        if (!a.venueVisible && b.venueVisible) {
          return -1
        }
        // Sort by venueRanking so lower prio venues are drawn first
        if (a.venueRanking !== b.venueRanking) {
          return a.venueRanking - b.venueRanking
        }
        // Then use the same sort as above
        if (!a.lat || !a.lng) {
          return 1
        }
        if (!b.lat || !b.lng) {
          return -1
        }
        if (a.lat < b.lat) {
          return -1
        } else if (a.lat > b.lat) {
          return 1
        }
        if (a.lng < b.lng) {
          return -1
        } else if (a.lng > b.lng) {
          return 1
        }
        if (a.venueName !== b.venueName) {
          return a.venueName - b.venueName
        }
        if (a.visible && b.visible) {
          return b.ranking - a.ranking
        } else if (a.visible) {
          return -1
        } else {
          return 1
        }
      })

      let prevLat = NaN
      let prevLng = NaN
      let prevVenueName = ""
      let prevVenueVisible = true
      let numSpaces = 0
      let prevMinPrice = 0
      let markerLabel = ""
      let venueContent
      for (const space of spaceData) {
        if (
          space.lat !== prevLat ||
          space.lng !== prevLng ||
          space.venueName !== prevVenueName
        ) {
          // Add a marker for the previous venue
          addVenueMarker(
            prevVenueName,
            prevLat,
            prevLng,
            markerLabel,
            prevVenueVisible,
            venueContent
          )
          // Build content for the next marker
          prevLat = space.lat
          prevLng = space.lng
          prevVenueName = space.venueName
          prevVenueVisible = space.venueVisible
          numSpaces = 0
          try {
            venueContent.remove()
          } catch (err) {}
          venueContent = document.createElement("div")
          venueContent.classList.add("map-info")
          let venueNameDiv = document.createElement("div")
          venueNameDiv.classList.add("space-heading-new")
          venueNameDiv.style.fontSize = "16px"
          venueNameDiv.style.paddingBottom = "10px"
          venueNameDiv.title = space.venueName
          venueNameDiv.innerText = space.venueName
          venueContent.appendChild(venueNameDiv)

          prevMinPrice = space.price
          markerLabel = "£"
          if (space.price > 0) {
            markerLabel += space.price
          }
        } else {
          // Still in the same venue, build up InfoWindow content
          // If more than one visible price OR more than one price and all invisible
          if (
            (space.visible || !space.venueVisible) &&
            space.price != prevMinPrice &&
            space.price > 0
          ) {
            if (prevMinPrice == 0) {
              markerLabel = "£" + space.price
            } else {
              prevMinPrice = Math.min(space.price, prevMinPrice)
              markerLabel = "£" + prevMinPrice + "+"
            }
          }
        }
        if (space.visible) {
          numSpaces++

          let spaceA = document.createElement("a")
          spaceA.href = space.href
          spaceA.target = "_blank"
          spaceA.style.color = "#1d1d1d"
          spaceA.style.textDecoration = "none"
          spaceA.style.textAlign = "left"
          spaceA.style.display = "block"
          spaceA.style.position = "relative"
          let slug = new URL(space.href).pathname.split("/").pop()
          spaceA.dataset.slug = slug
          gmapSpaceDict[slug] = {
            Slug: slug,
            Space: space.spaceName.replace("'", ""),
            Venue: space.venueName.replace("'", ""),
            "Venue + Space": space.venueSpaceName.replace("'", ""),
            Capacity: space.capacity,
            Price: space.price,
            "Selected From": "Map",
          }
          venueContent.appendChild(spaceA)

          let img = document.createElement("img")
          img.loading = "lazy"
          img.src = space.imgSrc
          if (space.imgSrcset) {
            img.srcset = space.imgSrcset
          }
          if (space.imgSizes) {
            img.sizes = space.imgSizes
          }
          img.style.width = "100%"
          img.style.height = "120px"
          img.style.objectFit = "cover"
          img.style.borderRadius = "6px"
          img.style.marginBottom = "6px"
          img.style.marginTop = "18px"
          if (numSpaces === 1) {
            // don't display top margin
            img.classList.add("first")
            img.style.marginTop = "0px"
          }
          spaceA.appendChild(img)

          let spaceTextDiv = document.createElement("div")
          spaceTextDiv.classList.add("flex-between")
          spaceA.appendChild(spaceTextDiv)

          if (space.availableTimesElement) {
            let availTimes = space.availableTimesElement.cloneNode(true)
            if (numSpaces === 1) {
              availTimes.style.top = "5px"
            } else {
              availTimes.style.top = "15px"
            }
            spaceA.appendChild(availTimes)
            availTimes.getElementsByClassName(
              "alternate-times-inner"
            )[0].style.maxWidth = "95%"
            if (availTimes.querySelectorAll(".pill").length > 0) {
              img.style.opacity = "0.3"
              spaceTextDiv.style.opacity = "0.5"
              gmapSpaceDict[slug]["Available Times"] =
                availTimes.getElementsByClassName("pill")[0].innerText
              let partialAvailabilitySuggestion = "Choose alternate times"
              availTimes.getElementsByClassName(
                "text-block-small"
              )[0].innerText = partialAvailabilitySuggestion
              gmapSpaceDict[slug]["Partial Availability"] =
                partialAvailabilitySuggestion
            } else {
              // Available an hour later or earlier.
              gmapSpaceDict[slug]["Partial Availability"] =
                "Available an hour later or earlier"
            }
          }

          let spaceNameDiv = document.createElement("div")
          spaceNameDiv.classList.add("space-heading-new")
          spaceNameDiv.title = space.spaceName
          if (!space.visible) {
            img.style.opacity = "0.3"
            spaceNameDiv.style.opacity = "0.3"
          }
          spaceNameDiv.innerText = space.spaceName
          spaceTextDiv.appendChild(spaceNameDiv)

          let fromPriceDiv = document.createElement("div")
          fromPriceDiv.classList.add("price-pill-new")
          fromPriceDiv.style.display = "flex"
          if (!space.visible) {
            fromPriceDiv.style.opacity = "0.3"
          }

          let priceDiv = document.createElement("div")
          priceDiv.classList.add("pill-content-new")
          priceDiv.innerText = "£" + space.price
          fromPriceDiv.appendChild(priceDiv)

          let perHourDiv = document.createElement("div")
          perHourDiv.classList.add("pill-secondary-new")
          perHourDiv.innerText = "/ hour"
          fromPriceDiv.appendChild(perHourDiv)

          spaceTextDiv.appendChild(fromPriceDiv)
        }
      }
      // Add a marker for the last venue
      addVenueMarker(
        prevVenueName,
        prevLat,
        prevLng,
        markerLabel,
        prevVenueVisible,
        venueContent
      )

      if (gmapLat && gmapLng) {
        // This is needed e.g. if user presses Near me, but location is far away
        // so gmapRefreshMarkers(true) is called after
        bounds.extend(new google.maps.LatLng(gmapLat, gmapLng))
      }

      if (redrawBounds) {
        resetBounds(bounds, true)
      }
      gmapRefreshMarkersRunOnce = true
    }

    async function resetBounds(
      bounds,
      userBoundsChange,
      minZoom,
      maxZoom,
      centreLatitude,
      centreLongitude
    ) {
      promiseMap.then(() => {
        gmapDontSetTextToMapArea = true
        // Don't zoom in too far if there's only one marker
        if (bounds.getNorthEast().equals(bounds.getSouthWest())) {
          let extendPoint1 = new google.maps.LatLng(
            bounds.getNorthEast().lat() + 0.001,
            bounds.getNorthEast().lng() + 0.001
          )
          let extendPoint2 = new google.maps.LatLng(
            bounds.getNorthEast().lat() - 0.001,
            bounds.getNorthEast().lng() - 0.001
          )
          bounds.extend(extendPoint1)
          bounds.extend(extendPoint2)
        }
        let initialZoomLevel = sharesyGmap.getZoom()
        sharesyGmap.userBoundsChange = userBoundsChange
        sharesyGmap.fitBounds(bounds)
        sharesyGmap.userBoundsChange = userBoundsChange
        sharesyGmap.fitBounds(bounds)
        gmapSavedBounds = bounds
        // Don't let map get too zoomed in or out
        if (minZoom && sharesyGmap.getZoom() < minZoom) {
          sharesyGmap.setZoom(minZoom)
        } else if (maxZoom && sharesyGmap.getZoom() > maxZoom) {
          sharesyGmap.setZoom(maxZoom)
        }
        if (centreLatitude && centreLongitude) {
          sharesyGmap.setCenter(
            new google.maps.LatLng(centreLatitude, centreLongitude)
          )
        }
        // gmapBoundsChange is called when 'dragend' or 'zoom_changed' fire.
        // So resetBounds will only trigger gmapBoundsChange if the new
        // bounds result in a zoom level change. If there is no zoom change
        // then we need to call it manually.
        // We could listen to 'bounds_changed' instead of this but that
        // fires a lot so can be a bit jumpy when the user is moving the map.
        if (initialZoomLevel === sharesyGmap.getZoom() && userBoundsChange) {
          gmapBoundsChange()
        }
        setTimeout(function () {
          gmapDontSetTextToMapArea = false
        }, 1000)
      })
    }

    async function setBoundsFromCentreLocation(
      centreLatitude,
      centreLongitude,
      radiusMiles,
      zoomOutIfNotEnoughSpaces
    ) {
      promiseMap.then(async () => {
        let distanceMetres = radiusMiles * 1609.344
        let origin = new google.maps.LatLng(centreLatitude, centreLongitude)
        let north = await google.maps.geometry.spherical.computeOffset(
          origin,
          distanceMetres,
          0
        )
        let west = await google.maps.geometry.spherical.computeOffset(
          origin,
          distanceMetres,
          -90
        )
        let south = await google.maps.geometry.spherical.computeOffset(
          origin,
          distanceMetres,
          180
        )
        let east = await google.maps.geometry.spherical.computeOffset(
          origin,
          distanceMetres,
          90
        )
        let bounds = new google.maps.LatLngBounds()
        bounds.extend(new google.maps.LatLng(north.lat(), west.lng()))
        bounds.extend(new google.maps.LatLng(south.lat(), east.lng()))
        resetBounds(bounds, true)
        if (zoomOutIfNotEnoughSpaces) {
          await unhideJetboostPages(20)
          // Wait for gmapBoundsChange to complete
          setTimeout(async function () {
            if (visibleSpaces() < 10) {
              try {
                let zoomOutAttempts = 0
                // in less densely populated areas we will zoom out until we find spaces or
                // it becomes apparent that there are few/no spaces in the surrounding area
                while (
                  visibleSpaces() < 10 - (zoomOutAttempts + 1) * 2 &&
                  zoomOutAttempts < 2
                ) {
                  sharesyGmap.setZoom(sharesyGmap.getZoom() - 1)
                  await util.sleep(1000)
                  gmapRefreshMarkers()
                  zoomOutAttempts++
                }
              } catch (err) {
                util.logToSentry(err)
              }
            }
          }, 1000)
        }
      })
    }

    function visibleSpaces() {
      const listitems = Array.from(
        document.getElementsByClassName("spaces-list-item")
      )
      let visibleSpaces = 0
      for (const l of listitems) {
        if (
          !l.classList.contains("area-hide") &&
          !l.classList.contains("map-hide") &&
          !l.classList.contains("hide") &&
          !l.classList.contains("hide-enquiry-only") &&
          !l.classList.contains("jetboost-list-item-hide")
        ) {
          visibleSpaces++
        }
      }
      return visibleSpaces
    }

    async function setBoundsForArea(
      minZoom,
      maxZoom,
      centreLatitude,
      centreLongitude
    ) {
      promiseMap.then(() => {
        let bounds = new google.maps.LatLngBounds()
        for (const space of spaceData) {
          if (!space.item.classList.contains("area-hide")) {
            if (space.lat && space.lng) {
              bounds.extend(new google.maps.LatLng(space.lat, space.lng))
            }
          }
        }
        resetBounds(
          bounds,
          false /* don't update list of spaces when map moves */,
          minZoom,
          maxZoom,
          centreLatitude,
          centreLongitude
        )
        refreshNumSpaces()
        refreshRemoveFilters()
      })
    }

    async function setAreaFromButton(
      areaButton,
      viaQueryParam,
      updateContentSections = false
    ) {
      let area = areaButton.querySelector(".icon-text").innerText
      let areaSlug = areaButton.querySelector(".slug-hidden").innerText
      document.getElementById("location-button-text").innerText = area
      removeSelectedLocation()
      removeFiltersLocation = area

      let level = parseInt(areaButton.querySelector(".level-hidden").innerText)
      let centreLatitude =
        areaButton.querySelector(".latitude-hidden").innerText
      let centreLongitude =
        areaButton.querySelector(".longitude-hidden").innerText
      if (level > util.LA_AREA_LEVEL) {
        // Eg 'Finchley' is a higher level # than LA, so it has no venues
        // assigned. Instead just set a centre location and radius.
        let radiusMiles = areaButton.querySelector(".radius-hidden").innerText
        setBoundsFromCentreLocation(
          centreLatitude,
          centreLongitude,
          radiusMiles,
          false
        )
      } else {
        // For LAs or their parent areas, get a list of LAs then filter to just
        // the spaces within those LAs.
        const areaButtons = document
          .getElementById("collection-list-area")
          .getElementsByClassName("icon-and-text-block-area w-inline-block")
        let areaSlugs = [areaSlug]
        while (level < util.LA_AREA_LEVEL) {
          level++
          for (const ab of areaButtons) {
            if (
              parseInt(ab.querySelector(".level-hidden").innerText) >= level &&
              areaSlugs.includes(
                ab.querySelector(".parentslug-hidden").innerText
              )
            ) {
              areaSlugs.push(ab.querySelector(".slug-hidden").innerText)
            }
          }
        }
        for (const space of spaceData) {
          space.item.classList.remove("map-hide")
          if (!areaSlugs.includes(space.laSlug)) {
            space.item.classList.add("area-hide")
          } else {
            space.item.classList.remove("area-hide")
          }
        }
        setBoundsForArea(11, 13, centreLatitude, centreLongitude)
      }
      if (!viaQueryParam) {
        closeModals()
      }
      // For SEO we want to display the page quickly so accept fewer results
      if (viaQueryParam) {
        unhideJetboostPages(10)
        unhideSpaceShortSummaries()
      } else {
        unhideJetboostPages(20)
      }
      if (updateContentSections) {
        // Update contentSectionItems, via the endpoint if not cached
        contentSectionItems = await getContentSections(areaSlug)
      }
      seoUpdates()
    }

    function setArea(area, viaQueryParam) {
      const areaButton = getAreaButton(area)
      setAreaFromButton(areaButton, viaQueryParam, false)
    }

    function processAreaFromUrl() {
      const areaButton = getAreaButton(getArea(searchParams))
      setAreaFromButton(areaButton, true, true)
      // Add SEO structured data
      let ratingValue = areaButton.querySelector(
        ".reviewscore-hidden"
      ).innerText
      let reviewCount = areaButton.querySelector(
        ".reviewcount-hidden"
      ).innerText
      let minPrice = areaButton.querySelector(".minprice-hidden").innerText
      let maxPrice = areaButton.querySelector(".maxprice-hidden").innerText
      let avgPrice = areaButton.querySelector(".avgprice-hidden").innerText
      if (ratingValue && reviewCount) {
        let json = {
          "@context": "http://schema.org",
          "@type": "LocalBusiness",
          name: ["Venue Hire"],
          url: window.location.href,
          currenciesAccepted: "gbp",
          paymentAccepted: "Credit Card, Debit Card",
          openingHours: "Mo-Su",
          logo: "https://uploads-ssl.webflow.com/5f5f7fb0593ba84e85cb2ac4/63fcceede020ee5b2b78b89d_Sharesy%20Logo-11-cropped1.png",
          image:
            "https://assets-global.website-files.com/5f5f802796e665f5a78839b3/6087fbee4bbdad7480af8ebe_CPMH01-p-800.jpeg",
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: ratingValue,
            reviewCount: reviewCount,
          },
        }
        if (avgPrice) {
          json["priceRange"] =
            "£" + minPrice + " - £" + maxPrice + " (Avg: £" + avgPrice + ")"
        }
        let ldScript = document.createElement("script")
        ldScript.type = "application/ld+json"
        ldScript.text = JSON.stringify(json)
        document.querySelector("head").appendChild(ldScript)
      }
    }

    function addUserLocationMarker() {
      // Remove user's location marker if there is one
      try {
        gmapMarker.setMap(null)
      } catch (err) {}
      if (gmapLat && gmapLng) {
        const markerLatLng = new google.maps.LatLng(gmapLat, gmapLng)
        const svgMarker = {
          path: "M12,2c-4.2,0-8,3.22-8,8.2c0,3.32,2.67,7.25,8,11.8c5.33-4.55,8-8.48,8-11.8C20,5.22,16.2,2,12,2z M12,12c-1.1,0-2-0.9-2-2 c0-1.1,0.9-2,2-2c1.1,0,2,0.9,2,2C14,11.1,13.1,12,12,12z",
          fillColor: "#1aa979",
          fillOpacity: 1,
          strokeWeight: 0,
          rotation: 0,
          scale: 2,
          anchor: new google.maps.Point(12, 22),
        }
        gmapMarker = new google.maps.Marker({
          position: markerLatLng,
          icon: svgMarker,
          map: sharesyGmap,
        })
        return markerLatLng
      }
    }

    function setUserLocation(locationButtonText) {
      // Use default text for postcode / near me searches
      removeFiltersLocation = "the area shown on the map"
      gmapDisplayDistancesFromUser()
      addUserLocationMarker()
      setBoundsFromCentreLocation(gmapLat, gmapLng, 2, true)
      document.getElementById("location-button-text").innerText =
        locationButtonText
    }

    /**
     * Creates a blur effect on the page to signal a delayed UI change to the user
     * @param {Element} extraElement
     * @param {number} timeOut
     */
    function blurSpaces(extraElement, timeOut = 800) {
      document.getElementById("spaces-list").classList.add("blur")
      if (extraElement) {
        extraElement.style.opacity = "40%"
      }
      setTimeout(function () {
        document.getElementById("spaces-list").classList.remove("blur")
        if (extraElement) {
          extraElement.style.opacity = "inherit"
        }
      }, timeOut)
    }

    function autoOpenCalendar(msDelay) {
      try {
        if (
          window
            .getComputedStyle(document.getElementById("location-modal"))
            .getPropertyValue("display") === "flex"
        ) {
          // If the user has already opened the location modal
          // then don't open the calendar
          return
        }
        if (
          !util.getSearchParams().has("date") &&
          !document
            .getElementById("date-button")
            .classList.contains("highlighted")
        ) {
          if (msDelay) {
            setTimeout(function () {
              document.getElementById("date-button").click()
            }, msDelay)
          } else {
            document.getElementById("date-button").click()
          }
        }
      } catch (err) {
        util.logToSentry(err)
      }
    }

    // Fired each time a date is selected in the date picker
    function flatpickrDateChange(selectedDates) {
      if (selectedDates.length > 0) {
        dateTextLong = selectedDates[0].toLocaleDateString("en-GB", {
          weekday: "short",
          month: "short",
          day: "numeric",
        })
        setDateButtonText()
        // Need to remove timezone offset before splitting ISO string
        // See: https://stackoverflow.com/a/50130338
        let d = new Date(
          selectedDates[0].getTime() -
            selectedDates[0].getTimezoneOffset() * 60000
        )
        date = d.toISOString().split("T")[0]
        util.updateUrlParam("date", date, false, true)

        try {
          logAmplitudeEvent("Select Date")
        } catch (err) {
          util.logToSentry(err)
        }

        // Clear the time buttons if time was previously selected
        const timeButtons = document.getElementsByClassName("time-button")
        for (const tb of timeButtons) {
          tb.classList.remove("selected")
          tb.classList.remove("auto-selected")
        }
        document.getElementById("time-modal").style.display = "flex"
        // If the date changes then we need to redo time filtering.
        // Blur for longer because this is often slower, and user is
        // distracted anyway with selecting the time.
        blurSpaces(null, 3000)
        // Let the UI update before running an expensive filter
        setTimeout(() => {
          filterByTime()
          scrollToTopIfNeeded()
          window.removeEventListener("scroll", closeCalendarOnScroll)
        }, 0)
      }
    }

    function setDateButtonText() {
      let dateText = document.getElementById("date-text")
      let timeText = document.getElementById("time-text")
      dateText.innerText = dateTextLong
      if (startTime && endTime) {
        timeText.innerText = startTime + "-" + endTime
      }
    }

    function sort(buttonId) {
      closeModals()
      let sortButton = document.getElementById(buttonId)
      let sortByAttribute = null
      if (sortButton.dataset.sortByAttribute) {
        sortByAttribute = sortButton.dataset.sortByAttribute
      }
      let sortDesc = sortButton.dataset.sortDesc === "true"
      blurSpaces(null, 800)
      setTimeout(() => {
        sortSpaces(
          sortButton.dataset.sortBy,
          sortByAttribute,
          sortDesc,
          sortButton.dataset.displayText
        )
        for (const b of document.querySelectorAll(".sort-button.selected")) {
          b.classList.remove("selected")
        }
        sortButton.classList.add("selected")
        unhideJetboostPages()
      }, 0)
    }

    // Sort the Space cards.
    function sortSpaces(
      sortBySelector,
      sortByAttribute,
      sortDescending,
      innerText
    ) {
      let list = document.querySelector("#spaces-list")
      if (list) {
        ;[...list.children]
          .sort(function (a, b) {
            let aIsOpaque = a
              .querySelector(".space-card-image-new")
              .classList.contains("opaque")
            let bIsOpaque = b
              .querySelector(".space-card-image-new")
              .classList.contains("opaque")
            let aHasLaterOrEarlier =
              !a
                .querySelector(".space-card-image-new")
                .classList.contains("opaque") &&
              a.querySelectorAll(".alternate-times").length > 0
            let bHasLaterOrEarlier =
              !b
                .querySelector(".space-card-image-new")
                .classList.contains("opaque") &&
              b.querySelectorAll(".alternate-times").length > 0
            // Sort first based on whether a card is opaque (i.e. whether there is availability for the selected time filters).
            if (aIsOpaque && !bIsOpaque) {
              return 1
            } else if (!aIsOpaque && bIsOpaque) {
              return -1
            }
            // Then sort if based on whether there is availability an hour later or earlier.
            else if (aHasLaterOrEarlier && !bHasLaterOrEarlier) {
              return 1
            } else if (!aHasLaterOrEarlier && bHasLaterOrEarlier) {
              return -1
            }
            // If both cards are available or partially available, then apply the specified sort order.
            else {
              let aValue
              let bValue
              if (sortByAttribute) {
                aValue = a
                  .querySelector(sortBySelector)
                  .getAttribute(sortByAttribute)
                bValue = b
                  .querySelector(sortBySelector)
                  .getAttribute(sortByAttribute)
              } else {
                aValue = a.querySelector(sortBySelector)?.innerText
                bValue = b.querySelector(sortBySelector)?.innerText
              }
              if (!util.isNumeric(aValue) && util.isNumeric(bValue)) {
                return 1
              }
              if (util.isNumeric(aValue) && !util.isNumeric(bValue)) {
                return -1
              }
              if (util.isNumeric(aValue) && util.isNumeric(bValue)) {
                aValue = Number(aValue)
                bValue = Number(bValue)
              }
              if (sortDescending) {
                return bValue - aValue
              } else {
                return aValue - bValue
              }
            }
          })
          .forEach((node) => list.appendChild(node))
      }

      // Only update text and width if those optional parameters are passed.
      if (typeof innerText !== "undefined") {
        // Update text on the sort dropdown
        const sortText = document.getElementById("sort-text")
        sortText.innerText = innerText
      }

      // Save global vars so they can be used when sort is called outside of pressing the sort button (e.g. filtering by time requires a sort).
      globalSortBySelector = sortBySelector
      globalSortByAttribute = sortByAttribute
      globalSortDescending = sortDescending
    }

    // Apply time filtering each time the filters change.
    async function filterByTime(shouldScrollToTop) {
      // Clear any previous time filter card changes
      const spaces = document.getElementsByClassName("space-card")
      for (const sc of spaces) {
        sc.parentElement.classList.remove("hide")
        sc.querySelector(".space-card-image-new").classList.remove("opaque")
        sc.querySelector(".space-identity-new").classList.remove("opaque")
        let alternateTimeElems = sc.querySelectorAll(".alternate-times")
        Array.from(alternateTimeElems).forEach((alternateTimeElem) =>
          alternateTimeElem.remove()
        )
        let minHoursElems = sc.querySelectorAll(".minimum-hours-container")
        Array.from(minHoursElems).forEach(
          (minHoursElem) => (minHoursElem.style.display = "none")
        )
        // Remove query string args if there were any, and set the date
        let url = new URL(sc.href)
        url.searchParams.delete("starttime")
        url.searchParams.delete("endtime")
        if (date) {
          url.searchParams.set("date", date)
        } else {
          url.searchParams.delete("date")
        }
        sc.href = url.href
      }

      if (date) {
        // if a date is selected, show the clear date button
        const clearDates = document.getElementById("clear-dates")
        clearDates.style.visibility = "visible"

        const dateAvail = await getAvailabilityData(date)

        // We have seen users on the search page with times selected but no date
        if (startTime && endTime) {
          const luxonStartTime = DateTime.fromISO(date + "T" + startTime, {
            zone: util.TIMEZONE,
          })
          const luxonEndTime = DateTime.fromISO(date + "T" + endTime, {
            zone: util.TIMEZONE,
          })
          let startTimestamp = luxonStartTime.ts
          let endTimestamp = luxonEndTime.ts
          let startTimestampOneHourEarlier = luxonStartTime.plus({
            hours: -1,
          }).ts
          let endTimestampOneHourEarlier = luxonEndTime.plus({ hours: -1 }).ts
          let startTimestampOneHourLater = luxonStartTime.plus({ hours: 1 }).ts
          let endTimestampOneHourLater = luxonEndTime.plus({ hours: 1 }).ts
          let sixPmTimestamp = DateTime.fromISO(date + "T18:00", {
            zone: util.TIMEZONE,
          }).ts
          let selectedMinutesDiff = luxonEndTime.diff(
            luxonStartTime,
            "minutes"
          ).minutes

          const totalHoursOverlap = (spaceTimes) => {
            const getHoursOverlap = (prev, curr) => {
              const highestStartTime = Math.max(
                startTimestamp,
                curr.start_time_utc
              )
              const lowestEndTime = Math.min(endTimestamp, curr.end_time_utc)
              const hoursDiff = Math.max(
                Math.floor((lowestEndTime - highestStartTime) / 1000 / 60 / 60),
                0
              )
              return prev + hoursDiff
            }
            return spaceTimes.reduce(getHoursOverlap, 0)
          }

          const isAvailableHourLater = (spaceTimes) => {
            return spaceTimes.find(
              (t) =>
                t.start_time_utc <= startTimestampOneHourLater &&
                t.end_time_utc >= endTimestampOneHourLater
            )
          }

          const isAvailableHourEarlier = (spaceTimes) => {
            return spaceTimes.find(
              (t) =>
                t.start_time_utc <= startTimestampOneHourEarlier &&
                t.end_time_utc >= endTimestampOneHourEarlier
            )
          }

          const hasFullAvailability = (spaceTimes) => {
            return spaceTimes.find(
              (t) =>
                t.start_time_utc <= startTimestamp &&
                t.end_time_utc >= endTimestamp
            )
          }

          const isAvailableTil6PM = (spaceTimes) => {
            return spaceTimes.find(
              (t) =>
                t.start_time_utc <= startTimestamp &&
                t.end_time_utc >= sixPmTimestamp
            )
          }

          for (const sc of spaces) {
            let slug = util.getSlugFromUrl(sc.href.toString())
            const spaceTimes = dateAvail[slug]
            let url = new URL(sc.href)
            if (spaceTimes && hasFullAvailability(spaceTimes)) {
              url.searchParams.set("starttime", startTime.replace(":", ""))
              url.searchParams.set("endtime", endTime.replace(":", ""))
              sc.href = url.href

              try {
                // warn the user their picked date range is less than the venue min hours
                const availTime = spaceTimes.find(
                  (t) =>
                    t.start_time_utc <= startTimestamp &&
                    t.end_time_utc >= endTimestamp
                )
                if (
                  availTime.min_duration_minutes > selectedMinutesDiff &&
                  sc.getElementsByClassName("youtube-hidden")[0].innerText
                    .length === 0
                ) {
                  // Only display if "virtual tour" pill isn't displayed
                  const minHours = sc.getElementsByClassName(
                    "minimum-hours-container"
                  )[0]
                  const pillText =
                    minHours.getElementsByClassName("pill-text")[0]
                  pillText.innerHTML = `${parseInt(
                    availTime.min_duration_minutes / 60
                  )}-hour minimum hire`
                  minHours.style.display = "flex"
                }
              } catch (err) {
                util.logToSentry(err)
              }
            } else if (spaceTimes) {
              // Don't add time params since they're not valid
              sc.href = url.href

              let hours =
                parseInt(endTime.substring(0, 2)) -
                parseInt(startTime.substring(0, 2))
              let hasMinAvail = false
              if (totalHoursOverlap(spaceTimes) / hours >= 0.66) {
                hasMinAvail = true
              }

              const availTil6pm = isAvailableTil6PM(spaceTimes)

              if (
                !isAvailableHourLater(spaceTimes) &&
                !isAvailableHourEarlier(spaceTimes) &&
                !availTil6pm &&
                !hasMinAvail &&
                util.getSearchParams().get("time") !== "internal"
              ) {
                // Hide the space.
                sc.parentElement.classList.add("hide")
              } else if (
                isAvailableHourLater(spaceTimes) ||
                isAvailableHourEarlier(spaceTimes)
              ) {
                // Venue is available an hour earlier or later
                let alternateTimes = document.createElement("div")
                sc.appendChild(alternateTimes)
                alternateTimes.classList.add("alternate-times")
                let alternateTimesInner = document.createElement("div")
                alternateTimes.appendChild(alternateTimesInner)
                alternateTimesInner.classList.add("alternate-times-inner")
                let extensionNote = document.createElement("div")
                alternateTimesInner.appendChild(extensionNote)
                extensionNote.classList.add("text-block-small")

                let alternateStart
                let alternateEnd
                let alternateText
                if (isAvailableHourLater(spaceTimes)) {
                  alternateStart = Intl.DateTimeFormat("en-GB", {
                    hour: "numeric",
                    minute: "numeric",
                  }).format(new Date(startTimestampOneHourLater))
                  alternateEnd = Intl.DateTimeFormat("en-GB", {
                    hour: "numeric",
                    minute: "numeric",
                  }).format(new Date(endTimestampOneHourLater))
                  alternateText = "later"
                } else {
                  alternateStart = Intl.DateTimeFormat("en-GB", {
                    hour: "numeric",
                    minute: "numeric",
                  }).format(new Date(startTimestampOneHourEarlier))
                  alternateEnd = Intl.DateTimeFormat("en-GB", {
                    hour: "numeric",
                    minute: "numeric",
                  }).format(new Date(endTimestampOneHourEarlier))
                  alternateText = "earlier"
                }
                extensionNote.innerHTML =
                  "Available an hour " +
                  alternateText +
                  ' (<span style="font-weight:600;white-space: nowrap;">' +
                  alternateStart +
                  "-" +
                  alternateEnd +
                  "</span>)"
                url.searchParams.set(
                  "starttime",
                  alternateStart.replace(":", "")
                )
                url.searchParams.set("endtime", alternateEnd.replace(":", ""))
                sc.href = url.href
              } else {
                // Mark the space as opaque and include availability.

                // Get the available times
                let times = spaceTimes.map((t) => {
                  let availStartTime = Intl.DateTimeFormat("en-GB", {
                    hour: "numeric",
                    minute: "numeric",
                  }).format(new Date(t.start_time_utc))
                  let availEndTime = Intl.DateTimeFormat("en-GB", {
                    hour: "numeric",
                    minute: "numeric",
                  }).format(new Date(t.end_time_utc))
                  return availStartTime + "-" + availEndTime
                })

                // Make the space card opaque
                sc.querySelector(".space-card-image-new").classList.add(
                  "opaque"
                )
                sc.querySelector(".space-identity-new").classList.add("opaque")

                // Add divs to display over the space
                let alternateTimes = document.createElement("div")
                sc.appendChild(alternateTimes)
                alternateTimes.classList.add("alternate-times")
                let alternateTimesInner = document.createElement("div")
                alternateTimes.appendChild(alternateTimesInner)
                alternateTimesInner.classList.add("alternate-times-inner")
                let availTimes = document.createElement("div")
                alternateTimesInner.appendChild(availTimes)
                availTimes.classList.add("mini-header")
                availTimes.innerText = "Available times"
                let pillContainer = document.createElement("div")
                alternateTimesInner.appendChild(pillContainer)
                pillContainer.classList.add("pill-container")
                for (const t of times) {
                  let pill = document.createElement("div")
                  pillContainer.appendChild(pill)
                  pill.classList.add("pill")
                  pill.innerText = t
                }
                let extensionNote = document.createElement("div")
                alternateTimesInner.appendChild(extensionNote)
                extensionNote.classList.add("text-block-small")
                if (availTil6pm || hasMinAvail) {
                  extensionNote.innerText =
                    "Click to choose from the available times."
                } else {
                  // Should only ever be seen by internal users
                  extensionNote.innerText = "LOW AVAILABILITY!"
                }
              }
            } else {
              sc.parentElement.classList.add("hide")
              //logAmplitudeEvent('Date is showing in Webflow AvailableDates list but isnt available');
            }
          }
        } else {
          // Date selected but no times. Just check if there is some availability on the selected date.
          for (const sc of spaces) {
            let slug = util.getSlugFromUrl(sc.href.toString())
            if (!dateAvail[slug]) {
              sc.parentElement.classList.add("hide")
            }
          }
        }
      }

      // Display all paged spaces in case the filter removes a lot of spaces
      unhideJetboostPages(20)
      // Run a sort (unavailable spaces are visible but sorted at the end)
      sortSpaces(
        globalSortBySelector,
        globalSortByAttribute,
        globalSortDescending
      )
      postFilterUpdates()
      if (shouldScrollToTop) {
        scrollToTopIfNeeded()
      }
    }

    /**
     * The page combines Jetboost filters (search + type) and custom JS filters
     * (date/time + location). Jetboost controls pagination by adding all
     * spaces to the page on page load then hiding those beyond the page limit.
     * When spaces are hidden and custom JS filtering hides a lot of spaces,
     * you see a small number of spaces followed by the See More button - i.e.
     * some items can remain hidden by Jetboost, leading to a confusing UX. I
     * don't see an easy way around this while we're combining Jetboost and
     * custom filtering, so I'm simulating clicks on Show More after any custom
     * JS filter action. We can also change the items per page and the initial
     * items to display in Jetboost.
     * @param minimumSpaces If provided the next page will only be added while
     * the number of spaces is less than minimumSpaces.
     */
    async function unhideJetboostPages(minimumSpaces) {
      if (jetboostPaginationUnhideComplete) {
        // We've previously already added all Jetboost pages. Use a global
        // variable to avoid using window.getComputedStyle which can take
        // over 300ms.
        return
      }
      runningUnhideJetboostPages = true
      try {
        if (minimumSpaces) {
          // Without a wait >100ms visibleSpaces is higher than it should be
          await new Promise((resolve) => setTimeout(resolve, 200))
          if (visibleSpaces() >= minimumSpaces) {
            runningUnhideJetboostPages = false
            return
          }
        }
        let showMore = document.getElementById("show-more")
        // Click Show More then wait until the button is hidden by Jetboost, max 10 times
        for (let i = 0; i < 10; i++) {
          const showMoreDisplay = window.getComputedStyle(showMore).display
          if (showMoreDisplay === "none") {
            jetboostPaginationUnhideComplete = true
            break
          }
          if (minimumSpaces) {
            await new Promise((resolve) => setTimeout(resolve, 200))
            if (visibleSpaces() >= minimumSpaces) {
              runningUnhideJetboostPages = false
              jetboostPaginationUnhideRunOnce = true
              return
            }
          }
          showMore.click()
          await new Promise((resolve) => setTimeout(resolve, 100))
        }
      } catch (err) {}
      runningUnhideJetboostPages = false
      jetboostPaginationUnhideRunOnce = true
    }

    function timeButtonClick(e) {
      let selectedButtons = document.getElementsByClassName(
        "time-button selected"
      )
      if (timeStatus === TIME_NONE) {
        timeStatus = TIME_START
        e.currentTarget.classList.add("selected")
        document.getElementById("time-modal-header").innerText =
          "Choose an end time"
        document.getElementById("time-modal-clear").style.display = "block"

        startTime = selectedButtons[0].innerText
        let startBtnTime = DateTime.fromISO(date + "T" + startTime, {
          zone: util.TIMEZONE,
        })
        const timeButtons = document.getElementsByClassName("time-button")
        for (const tb of timeButtons) {
          if (tb.innerText < startTime) {
            tb.classList.add("disabled")
          } else if (tb.innerText > startTime) {
            let btnTime = DateTime.fromISO(date + "T" + tb.innerText, {
              zone: util.TIMEZONE,
            })
            tb.addEventListener("mouseover", mouseoverSelectOtherTimes)
            tb.addEventListener("mouseout", mouseoutSelectOtherTimes)
          }
        }
        document.getElementById("time-modal").style.display = "none"
        setTimeout(function () {
          document.getElementById("time-modal").style.display = "flex"
        }, 300)
        try {
          logAmplitudeEvent("Select Start Time")
        } catch (err) {}

        util.updateUrlParam(
          "starttime",
          startTime.replace(":", ""),
          false,
          true
        )
      } else if (timeStatus === TIME_START) {
        startTime = selectedButtons[0].innerText
        endTime = e.currentTarget.innerText
        if (endTime > startTime) {
          timeStatus = TIME_NONE
          const timeButtons = document.getElementsByClassName("time-button")
          for (const tb of timeButtons) {
            if (tb.innerText < endTime && tb.innerText > startTime) {
              tb.classList.add("auto-selected")
            }
          }
          removeStartTimeEvents()
          e.currentTarget.classList.add("selected")
          document.getElementById("time-modal-header").innerText =
            "Choose a start time"
          document.getElementById("time-modal-clear").style.display = "block"
          blurSpaces(e.currentTarget, 1400)
          // Let the UI update before running an expensive filter
          setTimeout(() => {
            filterByTime()
          }, 0)
          try {
            logAmplitudeEvent("Select End Time")
          } catch (err) {}
          util.updateUrlParam("endtime", endTime.replace(":", ""), false, true)
          setDateButtonText()
          scrollToTopIfNeeded()
          closeModals(true, false, false)
        }
      } else if (timeStatus === TIME_END) {
        timeClear(false)
        timeButtonClick(e)
      }
    }

    function timeClear(filterSpaces = true) {
      const timeButtons = document.getElementsByClassName("time-button")
      for (const tb of timeButtons) {
        tb.classList.remove("selected")
        tb.classList.remove("auto-selected")
      }
      document.getElementById("time-modal-header").innerText =
        "Choose a start time"
      document.getElementById("time-modal-clear").style.display = "none"
      timeStatus = TIME_NONE
      try {
        logAmplitudeEvent("Clear Times")
      } catch (err) {}

      startTime = undefined
      endTime = undefined
      removeStartTimeEvents()
      setDateButtonText()
      if (filterSpaces) {
        blurSpaces(null, 1400)
        // Let the UI update before running an expensive filter
        setTimeout(() => {
          filterByTime()
        }, 0)
      }
    }

    function timeClose() {
      if (timeStatus === TIME_START) {
        timeClear()
      }
      closeModals()
      try {
        logAmplitudeEvent("Close Time Modal")
      } catch (err) {}
    }

    // If just a start time is selected, as the user mouses over potential end times, colour all the times in between.
    function mouseoverSelectOtherTimes(e) {
      if (timeStatus === TIME_START) {
        let mouseoverTime = e.currentTarget.innerText
        const timeButtons = document.getElementsByClassName("time-button")
        for (const tb of timeButtons) {
          if (tb.innerText < mouseoverTime && tb.innerText > startTime) {
            tb.classList.add("auto-selected")
          }
        }
      }
    }

    function mouseoutSelectOtherTimes(e) {
      if (timeStatus === TIME_START) {
        let mouseoverTime = e.currentTarget.innerText
        const timeButtons = document.getElementsByClassName("time-button")
        for (const tb of timeButtons) {
          if (tb.innerText < mouseoverTime && tb.innerText > startTime) {
            tb.classList.remove("auto-selected")
          }
        }
      }
    }

    function removeStartTimeEvents() {
      const timeButtons = document.getElementsByClassName("time-button")
      for (const tb of timeButtons) {
        tb.classList.remove("disabled")
        tb.removeEventListener("mouseover", mouseoverSelectOtherTimes)
        tb.removeEventListener("mouseout", mouseoutSelectOtherTimes)
      }
    }

    async function updatesAfterAvailabilityLoaded() {
      // As soon as availability is finished run these. They will be run again
      // once Jetboost pagination is complete (it's normally slower than
      // availability so we don't want to wait).
      processDateQueryParams()
      refreshNumSpaces()
      setPrices()
      promiseAvail.resolve()

      // Wait for Jetboost pagination to complete before setting prices etc for
      // *all spaces*. If Jetboost not finished after 4 secs then just go ahead.
      setTimeout(function () {
        promisePaginationTimeout = true
        promisePagination.resolve()
      }, 4000)
      await promisePagination.then(() => {
        postPaginationUpdates(true)
      })
      return Promise.resolve()
    }

    /* Keep an array of spaces and update it centrally, so we don't need to
     * regularly query the DOM for space data.
     */
    function updateSpaceData() {
      let spaces = []
      const listitems = Array.from(
        document.getElementsByClassName("spaces-list-item")
      )
      for (const l of listitems) {
        let space = {
          item: l,
          lat: parseFloat(l.querySelector(".latitude-hidden").innerText),
          lng: parseFloat(l.querySelector(".longitude-hidden").innerText),
          spaceName: l.querySelector(".space-name").innerText,
          venueName: l.querySelector(".venue-name").innerText,
          venueSpaceName: l.querySelector(".name-hidden").innerText,
          capacity: l.querySelector(".capacity-hidden").innerText,
          href: l.querySelector(".space-card").href,
          imgSrc: l.querySelector(".card-image-new").src,
          ranking:
            parseFloat(l.querySelector(".ranking-hidden").innerText) || -1,
          availableTimesElement: l.querySelector(".alternate-times"),
        }
        space["slug"] = new URL(space.href).pathname.split("/").pop()
        if (l.querySelector(".card-image-new").srcset) {
          space["imgSrcset"] = l.querySelector(".card-image-new").srcset
        }
        if (l.querySelector(".card-image-new").sizes) {
          space["imgSizes"] = l.querySelector(".card-image-new").sizes
        }
        if (l.querySelector(".localauthorityslug-hidden")) {
          space.laSlug = l.querySelector(".localauthorityslug-hidden").innerText
        } else {
          space.laSlug = null
        }
        spaces.push(space)
        l.querySelector(".space-card").addEventListener(
          "click",
          selectSpaceAmplitude
        )
        try {
          l.querySelector(".offer-pill").classList.remove("hide")
          l.querySelector(".pill-secondary-left").classList.add("hide")
        } catch (err) {
          util.logToSentry(err)
        }
      }
      spaceData = spaces
    }

    // Highlight the map icon when user hovers over a space card
    function spaceMapHoverListeners() {
      let width =
        window.innerWidth ||
        document.documentElement.clientWidth ||
        document.body.clientWidth
      if (width >= util.MAP_SPLIT_VIEW_WIDTH) {
        const spaceCards = document.getElementsByClassName("space-card")
        for (const s of spaceCards) {
          s.addEventListener("mouseover", function (e) {
            let lat =
              e.currentTarget.querySelector(".latitude-hidden").innerText
            let lng =
              e.currentTarget.querySelector(".longitude-hidden").innerText
            let venueName =
              e.currentTarget.querySelector(".venue-name").innerText
            let marker =
              gmapDivMarkerDict[
                [
                  venueName,
                  parseFloat(lat).toFixed(4),
                  parseFloat(lng).toFixed(4),
                ]
              ]
            // If the user hovers while markers are being removed or added this will fail so ignore error
            try {
              marker.highlight()
            } catch (err) {}
          })
          s.addEventListener("mouseout", function (e) {
            let lat =
              e.currentTarget.querySelector(".latitude-hidden").innerText
            let lng =
              e.currentTarget.querySelector(".longitude-hidden").innerText
            let venueName =
              e.currentTarget.querySelector(".venue-name").innerText
            let marker =
              gmapDivMarkerDict[
                [
                  venueName,
                  parseFloat(lat).toFixed(4),
                  parseFloat(lng).toFixed(4),
                ]
              ]
            try {
              marker.unhighlight()
            } catch (err) {}
          })
        }
      }
    }

    function setDateQueryParams() {
      const params = util.getSearchParams()
      if (params.has("date")) {
        date = params.get("date")
      } else {
        // Hide the clear dates 'X'
        document.getElementById("clear-dates").style.visibility = "hidden"
      }
    }

    /**
     * Trigger UI updates relating to date params
     */
    function processDateQueryParams() {
      const params = util.getSearchParams()
      if (params.has("date")) {
        fp.setDate(date)
        let d = new Date(date)
        dateTextLong = d.toLocaleDateString("en-GB", {
          weekday: "short",
          month: "short",
          day: "numeric",
        })
        if (params.has("starttime") && params.has("endtime")) {
          startTime =
            params.get("starttime").substring(0, 2) +
            ":" +
            params.get("starttime").substring(2, 4)
          endTime =
            params.get("endtime").substring(0, 2) +
            ":" +
            params.get("endtime").substring(2, 4)
        }
        filterByTime()
        setDateButtonText()
      }
    }

    /**
     * Sets the prices on the space cards and map
     * The initial value is obtained on page load
     * from `getPricesData`
     */
    function setPrices() {
      const spaces = document.getElementsByClassName("space-card")
      for (const sc of spaces) {
        let slug = util.getSlugFromUrl(sc.href.toString())
        try {
          const minPrice = prices[slug]
          if (!isNaN(minPrice)) {
            sc.querySelector(".price").innerText = Math.ceil(minPrice)
            sc.querySelector(".price-pill-new").style.display = "flex"
          }
        } catch (err) {
          let contextDict = {
            contexts: {
              context: {
                slug: slug,
              },
            },
          }
          // For now don't log to Sentry as we're hitting our quota - it's
          // sometimes an expected error. For now just send to Amplitude.
          // util.logToSentry(err, contextDict);
          const eventProperties = {
            "Slug Missing Price": slug,
          }
          logAmplitudeEvent("Missing a Price", eventProperties)
        }
      }
    }

    /**
     * Fires an Amplitude event every time a space is visible on a user's screen.
     *
     * Uses the IntersectionObserver API: https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API
     *
     * Called from `updatesAfterAvailabilityLoaded` so that initial filtering/pagination doesn't cause the observers to misfire.
     */
    function addSpaceImpressionObservers() {
      try {
        const options = { threshold: 0.6 } // only fire event if the space is in view i.e. not just a pixel
        const observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const e = entry.target
              const slug = new URL(e.href).pathname.split("/").pop()
              const eventProperties = {
                Slug: slug,
                Space: e
                  .querySelector(".space-name")
                  .innerText.replace("'", ""),
                Venue: e
                  .querySelector(".venue-name")
                  .innerText.replace("'", ""),
                "Venue + Space": e
                  .querySelector(".name-hidden")
                  .innerText.replace("'", ""),
                Capacity: e.querySelector(".capacity-hidden").innerText,
                Price: e.querySelector(".price").innerText,
              }
              const alternateTimes = getAlternateTimesData(e)
              eventProperties["Partial Availability"] =
                alternateTimes["Partial Availability"]
              eventProperties["Available Times"] =
                alternateTimes["Available Times"]
              logAmplitudeEvent("Space Impression", eventProperties)
            }
          })
        }, options)
        const spaceCards = document.querySelectorAll(".space-card")
        spaceCards.forEach((card) => observer.observe(card))
      } catch (err) {
        util.logToSentry(err)
      }
    }

    function closeModals(
      closeDate = true,
      closeSort = true,
      closeLocation = true
    ) {
      for (const p of document.getElementsByClassName("popup-background")) {
        p.classList.remove("dark")
        document.getElementById("sort-container").style.zIndex = "1"
        p.style.display = "none"
      }
      document.getElementById("search-widget").classList.remove("highlighted")
      if (closeSort) {
        document.getElementById("sort-modal").style.display = "none"
        document.getElementById("sort-button").classList.remove("highlighted")
      }
      if (closeLocation) {
        document.getElementById("location-modal").style.display = "none"
        document
          .getElementById("location-button")
          .classList.remove("highlighted")
        document
          .getElementById("location-button")
          .parentNode.classList.remove("highlighted")
      }
      if (closeDate) {
        document.getElementById("time-modal").style.display = "none"
        document.getElementById("date-button").classList.remove("highlighted")
        document
          .getElementById("date-button")
          .parentNode.classList.remove("highlighted")
        window.removeEventListener("scroll", closeCalendarOnScroll)
        // If you click the Choose date button a second time date can reopen after calling close, so add a delay
        setTimeout(function () {
          fp.close()
        }, 100)
      }
    }

    function closeCalendarOnScroll() {
      if (
        document.getElementById("date-button").classList.contains("highlighted")
      ) {
        closeModals(true, true, true)
      }
    }

    function removeSelectedLocation() {
      if (gmapLat || gmapLng) {
        gmapLat = null
        gmapLng = null
        try {
          // Remove the marker if there is one
          gmapMarker.setMap(null)
        } catch (err) {}

        // Reset addresses
        const listitems = document.getElementsByClassName("spaces-list-item")
        for (const l of listitems) {
          let addressVenue = l.querySelector(".address-venue")
          let originalText = addressVenue.dataset.originalText
          // It may not have been set yet
          if (originalText) {
            addressVenue.innerText = originalText
          }
          let addressSpace = l.querySelector(".address-space")
          originalText = addressSpace.dataset.originalText
          if (originalText) {
            addressSpace.innerText = originalText
          }
        }

        sort("sort-order-high-to-low")
        document.getElementById("distance-low-to-high").style.display = "none"
      }
    }

    // Allow users to view spaces outside of the selected map area.
    // Only display the section when the "Show More" pagination button is hidden.
    async function refreshRemoveFilters() {
      let removeFilters = document.getElementById("remove-filters")
      let spacesHiddenByMap = 0
      try {
        spacesHiddenByMap = document.querySelectorAll(
          ".map-hide:not(.hide,.jetboost-list-item-hide),.area-hide:not(.hide,.jetboost-list-item-hide)"
        ).length
      } catch (err) {
        util.logToSentry(err)
      }
      if (
        spacesHiddenByMap > 0 &&
        window.getComputedStyle(document.getElementById("show-more"))
          .display === "none" &&
        removeFiltersLocation !== "London"
      ) {
        removeFilters.style.display = "block"
        document.getElementById("remove-filters-num-spaces").innerText =
          spacesHiddenByMap
        document.getElementById("remove-filters-location").innerText =
          removeFiltersLocation
      } else {
        removeFilters.style.display = "none"
      }
    }

    async function refreshNumSpaces() {
      if (readyToShowNumSpaces) {
        let numSpaces = document.querySelectorAll(".spaces-list-item").length
        try {
          let afterNonJetboostFilters =
            numSpaces -
            document.querySelectorAll(
              ".spaces-list-item.hide,.spaces-list-item.area-hide,.spaces-list-item.map-hide"
            ).length
          numSpaces = afterNonJetboostFilters
        } catch (err) {
          util.logToSentry(err)
        }
        if (isJetboostFiltering()) {
          try {
            let afterAllFilters = numSpaces
            let afterJetboostFilters =
              document.getElementById("num-spaces-hidden").innerText
            if (afterJetboostFilters != "0") {
              let filtersExcludingJetboost = document.querySelectorAll(
                ".spaces-list-item.hide:not(.jetboost-list-item-hide),.spaces-list-item.area-hide:not(.jetboost-list-item-hide),.spaces-list-item.map-hide:not(.jetboost-list-item-hide)"
              ).length
              afterAllFilters = afterJetboostFilters - filtersExcludingJetboost
            }
            numSpaces = Math.min(afterAllFilters, numSpaces)
          } catch (err) {
            util.logToSentry(err)
          }
        }
        document.getElementById("num-spaces").innerText = numSpaces
        document.getElementById("map-space-count").innerText = numSpaces
        document.getElementById("info-number-spaces").innerText = numSpaces
        document
          .getElementById("map-space-count-container")
          .classList.remove("hide")
        document.getElementById("num-spaces-container").classList.remove("hide")
      }
    }

    function isJetboostFiltering() {
      const sp = util.getSearchParams()
      return sp.has("types") || sp.has("search")
    }

    function getArea(params) {
      let area = window.location.pathname.split("/").pop()
      if (params.has("area")) {
        area = params.get("area")
      }
      return area
    }

    function getAreaButton(area) {
      const areaButtons = document
        .getElementById("collection-list-area")
        .getElementsByClassName("icon-and-text-block-area w-inline-block")
      for (const areaButton of areaButtons) {
        if (areaButton.querySelector(".slug-hidden").innerText === area) {
          return areaButton
        }
      }
    }

    function seoUpdates() {
      const sp = util.getSearchParams()
      const type = sp.get("types")
      const area = getArea(sp)
      const loc = sp.get("loc")

      let h1 = defaultH1
      let metaDesc = defaultMetaDesc
      let pageTitle = defaultPageTitle

      let mainH2 = defaultMainH2
      let mainH2Body = defaultMainH2Body
      let locationListDisplay = "block"
      let typeListDisplay = "block"

      let typeCanonicalLocationLevel = "0"

      let areaName = null
      let postcodes = null
      let areaUrl = null
      let areaLevel = null
      let areaParentSlug = null
      let areaReviewScore = null
      let areaReviewCount = null
      let areaMinPrice = null
      let areaMaxPrice = null
      let areaAvgPrice = null

      let breadcrumbL1Display = "none"
      let breadcrumbL2Display = "none"
      let breadcrumbL2LinkUrl = null
      let breadcrumbL2LinkText = null
      let breadcrumbL3Display = "none"
      let breadcrumbL3LinkUrl = null
      let breadcrumbL3LinkText = null
      let breadcrumbCurrentPageText = "London"
      let typeName = null

      if (type) {
        // Remove selected-tab class so All item doesn't appear selected.
        document.getElementById("all-types").classList.remove("selected-tab")

        // Return if the typeItems array hasn't been populated yet
        if (typeItems.length === 0) {
          return
        }
        let typeItem = typeItems.find(({ slug }) => slug === type)
        h1 = typeItem.h1
        metaDesc = typeItem.metaDesc
        pageTitle = typeItem.pageTitle
        typeCanonicalLocationLevel = typeItem.typeCanonicalLocationLevel
        typeName = typeItem.name
        typeListDisplay = "none"
      } else {
        // There is no type selected so select the All type
        document.getElementById("all-types").classList.add("selected-tab")
      }

      // Don't set any SEO values if this function is called via Jetboost before init() has run (but we still want to set the selected-tab class)
      if (!defaultH1) {
        return
      }

      // Find the name and postcode list for the Area from the Location dropdown
      for (const a of document
        .getElementById("collection-list-area")
        .getElementsByClassName("icon-and-text-block-area")) {
        if (a.getElementsByClassName("slug-hidden")[0].innerText === area) {
          areaName = a.getElementsByClassName("icon-text")[0].innerText
          postcodes = a.getElementsByClassName("postcodes-hidden")[0].innerText
          areaUrl = a.getElementsByClassName("searchurl-hidden")[0].innerText
          areaLevel = a.getElementsByClassName("level-hidden")[0].innerText
          areaParentSlug =
            a.getElementsByClassName("parentslug-hidden")[0].innerText
          areaReviewScore =
            a.getElementsByClassName("reviewscore-hidden")[0].innerText
          areaReviewCount =
            a.getElementsByClassName("reviewcount-hidden")[0].innerText
          areaMinPrice =
            a.getElementsByClassName("minprice-hidden")[0].innerText
          areaMaxPrice =
            a.getElementsByClassName("maxprice-hidden")[0].innerText
          areaAvgPrice =
            a.getElementsByClassName("avgprice-hidden")[0].innerText
          break
        }
      }
      if (type) {
        pageTitle = pageTitle.replace(defaultLocation, areaName)
        metaDesc = metaDesc.replace(defaultLocation, areaName)
      }
      let h2Text = areaName
      if (postcodes) {
        h2Text += " (" + postcodes + ")"
      }
      mainH2 = mainH2.replace(defaultLocation, h2Text)
      mainH2Body = mainH2Body.replace(defaultLocation, areaName)
      locationListDisplay = "none"
      let canonicalUrl = new URL(areaUrl)

      if (loc) {
        h1 = h1.replace("in " + defaultLocation, "near " + loc)
      } else {
        h1 = h1.replace(defaultLocation, areaName)
      }

      if (areaParentSlug) {
        // Display the extra L1 link in breadcrumbs, and set the page text to the area name
        breadcrumbL1Display = "flex"
        if (areaParentSlug === "london" || areaLevel === "2") {
          if (type && typeCanonicalLocationLevel > "1") {
            breadcrumbL2Display = "flex"
            breadcrumbL2LinkUrl = canonicalUrl.href
            breadcrumbL2LinkText = areaName
            canonicalUrl.searchParams.set("types", type)
            breadcrumbCurrentPageText = typeName
          } else {
            breadcrumbCurrentPageText = areaName
          }
        } else {
          // Level 3
          breadcrumbL2Display = "flex"
          breadcrumbL2LinkUrl = canonicalUrl.href.substring(
            0,
            canonicalUrl.href.lastIndexOf("/")
          )
          breadcrumbL2LinkText = util.capitalisedNameFromSlug(
            breadcrumbL2LinkUrl.split("/").pop()
          )
          if (type && typeCanonicalLocationLevel > "2") {
            breadcrumbL3Display = "flex"
            breadcrumbL3LinkUrl = canonicalUrl.href
            breadcrumbL3LinkText = areaName
            canonicalUrl.searchParams.set("types", type)
            breadcrumbCurrentPageText = typeName
          } else {
            breadcrumbCurrentPageText = areaName
          }
        }
      } else {
        if (type && typeCanonicalLocationLevel > "0") {
          canonicalUrl.searchParams.set("types", type)
          breadcrumbL1Display = "flex"
          breadcrumbCurrentPageText = typeName
        }
      }

      // Set the SEO values. Do this before the 'await' below so e.g. the
      // heading is updated immediately.

      // There can be multiple 'link' elements if we add one in the Webflow head section
      // for this page (another one is added automatically because we have set the
      // "Global canonical tag URL" in the Webflow settings after changing the site to
      // run on webflow.sharesy.com).
      document.querySelectorAll('link[rel="canonical"]').forEach((elem) => {
        elem.href = canonicalUrl.href
      })

      document.title = pageTitle
      document.getElementById("h1-title").innerText = h1
      document
        .querySelector('meta[name="description"]')
        .setAttribute("content", metaDesc)
      document.getElementById("main-h2").innerText = mainH2
      document.getElementById("main-h2-body").innerText = mainH2Body
      // Set the breadcrumbs underneath the search results
      document.getElementById("breadcrumb-l1").style.display =
        breadcrumbL1Display
      document.getElementById("breadcrumb-l2").style.display =
        breadcrumbL2Display
      document.getElementById("breadcrumb-l3").style.display =
        breadcrumbL3Display
      if (breadcrumbL2LinkUrl) {
        document.getElementById("breadcrumb-l2-link").href = breadcrumbL2LinkUrl
        document.getElementById("breadcrumb-l2-link").innerText =
          breadcrumbL2LinkText
      }
      if (breadcrumbL3LinkUrl) {
        document.getElementById("breadcrumb-l3-link").href = breadcrumbL3LinkUrl
        document.getElementById("breadcrumb-l3-link").innerText =
          breadcrumbL3LinkText
      }
      document.getElementById("breadcrumb-current-page").innerText =
        breadcrumbCurrentPageText
      // Set the visibility of the 3 sections with links
      document.getElementById("location-list").style.display =
        locationListDisplay
      let eventTypeList = document.getElementById("event-type-list")
      eventTypeList.style.display = typeListDisplay
      eventTypeList
        .querySelectorAll(".text-link-underlined")
        .forEach((elem) => {
          let elemUrl = new URL(elem.href)
          elem.href = areaUrl + "?" + elemUrl.searchParams.toString()
        })
      let venueTypeList = document.getElementById("venue-type-list")
      venueTypeList.style.display = typeListDisplay
      venueTypeList
        .querySelectorAll(".text-link-underlined")
        .forEach((elem) => {
          let elemUrl = new URL(elem.href)
          elem.href = areaUrl + "?" + elemUrl.searchParams.toString()
        })

      // Refresh the "Heading" ContentSections
      let contentSectionHeadings = document.getElementById(
        "contentsection-headings"
      )
      // Remove all sections
      while (contentSectionHeadings.firstChild) {
        contentSectionHeadings.removeChild(contentSectionHeadings.firstChild)
      }
      let faqHeader = document.getElementById("faq-h2")
      faqHeader.classList.add("hide")
      if (contentSectionItems) {
        // Add new ones
        let sections = contentSectionItems.filter(
          (cs) =>
            cs.section === "Heading" &&
            ((!cs.type && !type) || cs.type === type) &&
            cs.area === area
        )
        sections.forEach((cs) => {
          let h2 = document.createElement("h2")
          h2.classList.add("standard-h2")
          h2.innerText = cs.heading
          contentSectionHeadings.appendChild(h2)
          let body = document.createElement("div")
          body.classList.add("w-richtext")
          body.innerHTML = cs.body
          contentSectionHeadings.appendChild(body)
        })
        // Refresh the "FAQ" ContentSections
        let contentSectionFaqs = document.getElementById("contentsection-faqs")
        while (contentSectionFaqs.firstChild) {
          contentSectionFaqs.removeChild(contentSectionFaqs.firstChild)
        }
        sections = contentSectionItems.filter(
          (cs) =>
            cs.section === "FAQ" &&
            ((!cs.type && !type) || cs.type === type) &&
            ((!cs.area && !area) || cs.area === area)
        )
        sections.forEach((cs) => {
          let h3 = document.createElement("h3")
          h3.classList.add("standard-h3")
          h3.innerText = cs.heading
          contentSectionFaqs.appendChild(h3)
          let body = document.createElement("div")
          body.classList.add("w-richtext")
          body.innerHTML = cs.body
          contentSectionFaqs.appendChild(body)
        })
        if (sections.length > 0) {
          faqHeader.classList.remove("hide")
        }
      }
    }

    function selectNearMe() {
      function success(position) {
        gmapLat = position.coords.latitude
        gmapLng = position.coords.longitude
        gmapPostcode = "you"
        setUserLocation("Near me")
        util.deleteUrlParam("loc")
        util.deleteUrlParam("area")
        scrollToTopIfNeeded()
        autoOpenCalendar(1000)
        const eventProperties = {
          Latitude: gmapLat,
          Longitude: gmapLng,
        }
        logAmplitudeEvent("Near Me Succeeded", eventProperties)
      }

      function error() {
        closeModals()
        document.getElementById("near-me-div").style.display = "none"
        const eventProperties = {
          Failure: "User didnt give permission",
        }
        logAmplitudeEvent("Near Me Failed", eventProperties)
      }

      if (!navigator.geolocation) {
        closeModals()
        document.getElementById("near-me-div").style.display = "none"
        const eventProperties = {
          Failure: "Not supported by browser",
        }
        logAmplitudeEvent("Near Me Failed", eventProperties)
      } else {
        closeModals()
        blurSpaces(null, 5000)
        // Let the UI update before running an expensive filter
        setTimeout(() => {
          navigator.geolocation.getCurrentPosition(success, error)
        }, 0)
      }
    }

    function showListView(updateUrl = true) {
      closeModals()
      document
        .getElementById("filter-section")
        .classList.remove("no-top-margin")
      document.getElementById("map-column").style.visibility = "hidden"
      document.getElementsByClassName("below-map")[0].style.display = "none"
      document.getElementById("results-column").style.display = "block"
      document.getElementById("floating-list-button").style.display = "none"
      document.getElementById("floating-map-button").style.display = "flex"
      if (updateUrl) {
        util.updateUrlParam("view", "list", { type: "list" }, false)
      }
      document.body.classList.remove("noscroll")
      if (
        showLocationPopupCount < 2 &&
        mapAreaChanged &&
        document.getElementsByClassName("map-hide").length > 0
      ) {
        document.getElementById("location-help").style.display = "block"
        showLocationPopupCount++
        mapAreaChanged = false
        logAmplitudeEvent("Location Filter Warning")
      }
    }

    function selectShowMap(updateUrl = true) {
      closeModals()
      // Without this when the user scrolls the header above the map down you
      // can see the map through the margin.
      document.getElementById("filter-section").classList.add("no-top-margin")
      let filterHeight = document.querySelector(".filter-section").scrollHeight
      if (
        window.getComputedStyle(document.getElementById("search-form"))
          .position === "sticky"
      ) {
        // Mobile view - search-header will be hidden when user switches to map view
        filterHeight += document.getElementById("search-form").scrollHeight
      } else {
        // Desktop view - search-form has position:fixed and displays in the centre above search-header
        filterHeight +=
          document.getElementsByClassName("search-header")[0].scrollHeight
      }
      const belowMap = document.getElementsByClassName("below-map")[0]
      belowMap.style.display = "block"
      belowMap.style.height = "calc(100vh - " + filterHeight + "px)"
      let mapColumn = document.getElementById("map-column")
      mapColumn.style.top = filterHeight + "px"
      mapColumn.style.visibility = "visible"
      document.getElementById("results-column").style.display = "none"
      document.getElementById("floating-map-button").style.display = "none"
      document.getElementById("floating-list-button").style.display = "flex"
      if (updateUrl) {
        util.updateUrlParam("view", "map", { type: "map" }, false)
      }
      // Scroll down so the header shrinks up (there is some margin above the filter section that we want to scroll away)
      window.scrollTo(0, 500)
      // Disable scrolling (otherwise the user can scroll the header section and the map shows through the margin)
      document.body.classList.add("noscroll")
    }

    async function populateTypeItems() {
      try {
        const response = await fetch("/types-list")
        const html = await response.text()
        const doc = new DOMParser().parseFromString(html, "text/html")
        const typeCollectionItems = doc.getElementsByClassName("w-dyn-item")
        for (const t of typeCollectionItems) {
          let typeUrl = t.querySelector(".type-name")
          typeItems.push({
            slug: typeUrl.href.substring(typeUrl.href.lastIndexOf("/") + 1),
            name: typeUrl.innerText,
            h1: t.querySelector(".type-h1").innerText,
            metaDesc: t.querySelector(".type-meta-desc").innerText,
            pageTitle: t.querySelector(".type-page-title").innerText,
            typeCanonicalLocationLevel: t.querySelector(
              ".type-canonical-location-level"
            ).innerText,
          })
        }
        seoUpdates()
      } catch (err) {
        util.logToSentry(err)
        logAmplitudeEvent("Populate Types Returned Error")
      }
    }

    /**
     * Get the SEO content for a given area.
     * Uses a memoized call to API endpoint 'get-content-sections'.
     * @param {string} area
     * @returns object contentSections
     */
    async function getContentSections(area) {
      try {
        if (area && contentSectionsMap.hasOwnProperty(area)) {
          return contentSectionsMap[area]
        }
        let endpoint = "get-content-sections"
        if (area) {
          endpoint += "?area=" + area
        }
        let contentSections = await util.api.get(endpoint).json()
        if (area) {
          contentSectionsMap[area] = contentSections
        }
        return contentSections
      } catch (err) {
        util.logToSentry(err)
        logAmplitudeEvent("Populate Content Sections Returned Error")
      }
    }

    async function postFilterUpdates() {
      if (gmapRefreshMarkersRunOnce) {
        gmapRefreshMarkers(false)
      }
      refreshNumSpaces()
      refreshRemoveFilters()
      updateSpaceShortSummaries()
    }

    function jetboostFilterComplete(listWrapperNode) {
      // When the page loads Jetboost will call this once for each filter or
      // pagination (in our case 4 times). Use a flag to block some of these
      // from running.
      if (runningJetboostFilterComplete) {
        return
      }
      // This will be called for all Jetboost collection lists on the page, so
      // only do stuff for the spaces list.
      if (listWrapperNode.id === "spaces-list-wrapper") {
        runningJetboostFilterComplete = true
        setTimeout(function () {
          runningJetboostFilterComplete = false
        }, 200)
        // When Jetboost filters run they add paging back in. If we have already
        // removed paging earlier then remove it again.
        if (jetboostPaginationUnhideRunOnce && !runningUnhideJetboostPages) {
          jetboostPaginationUnhideComplete = false
          unhideJetboostPages(20)
        }
        postFilterUpdates()
        seoUpdates()
      }
    }

    function jetboostPaginationComplete(collectionListWrapperElement) {
      // Jetboost pagination applies to all collection lists on the page, so
      // only do stuff for the spaces list
      if (collectionListWrapperElement.id === "spaces-list-wrapper") {
        promisePagination.resolve()
        if (promisePaginationTimeout) {
          // Rerun necessary functions (they have already run but before
          // pagination completed so not all spaces are included). If
          // promisePagination was resolved via timeout right before this runs,
          // some of the updates will still be running (e.g.
          // setBoundsFromCentreLocation has a 1 sec timeout). Wait 1.5 sec to
          // allow these other things to finish.
          setTimeout(function () {
            postPaginationUpdates(false)
            // Don't show the number of spaces until pagination is complete,
            // otherwise we'll show the wrong number.
            setTimeout(() => {
              readyToShowNumSpaces = true
              refreshNumSpaces()
              refreshRemoveFilters()
            }, 1000)
          }, 1500)
        } else {
          setTimeout(() => {
            readyToShowNumSpaces = true
            refreshNumSpaces()
            refreshRemoveFilters()
          }, 1000)
        }

        try {
          for (const sc of document.getElementsByClassName("space-card")) {
            // Display virtual tour pills for spaces that have them
            if (
              sc.getElementsByClassName("youtube-hidden")[0].innerText.length >
              0
            ) {
              sc.getElementsByClassName(
                "virtual-tour-container"
              )[0].style.display = "flex"
            }

            // Move the virtual tour and min hours pills to the bottom left so we can display the free cancellation pill
            sc.getElementsByClassName(
              "free-cancellation-container"
            )[0].style.display = "flex"
            sc.getElementsByClassName(
              "virtual-tour-container"
            )[0].classList.add("bottom-left")
            sc.getElementsByClassName(
              "minimum-hours-container"
            )[0].classList.add("bottom-left")
          }
        } catch (err) {
          util.logToSentry(err)
        }
      }
    }

    function postPaginationUpdates(firstRun) {
      updateSpaceData()
      processDateQueryParams()
      if (!searchParams.get("loc")) {
        // Run this again after pagination otherwise num spaces will be wrong
        setArea(getArea(util.getSearchParams()), true)
      }
      setPrices()
      if (firstRun) {
        addSpaceImpressionObservers()
        spaceMapHoverListeners()
        try {
          const spaces = document.getElementsByClassName("space-card")
          for (const sc of spaces) {
            let kitchenSection = sc.querySelector(".kitchen-indicator")
            let kitchenType = kitchenSection.querySelector(
              ".kitchen-type-hidden"
            ).innerText
            if (kitchenType.startsWith("Kitchenette")) {
              kitchenSection.querySelector(
                ".kitchen-icon-and-text"
              ).lastChild.innerText = "Kitchenette"
            }
          }
        } catch (err) {
          util.logToSentry(err)
        }
      }
    }

    /**
     * Load prices for all spaces. If a date is selected, get avaialility data
     * so it is cached when it is needed.
     */
    async function initialAvailabilityData() {
      try {
        updateSpaceData()
        setDateQueryParams()
        if (date) {
          await Promise.all([getAvailabilityData(date), getPricesData()])
        } else {
          await getPricesData()
        }
        await updatesAfterAvailabilityLoaded()
      } catch (err) {
        util.logToSentry(err)
      }
    }

    /**
     * Gets price data for all spaces
     * Set in space cards in `setPrices` and used in the map pins
     */
    async function getPricesData() {
      try {
        prices = await util.api
          .get("get-prices-combined", { retry: { limit: 4 } })
          .json()
      } catch (error) {
        util.logToSentry(error)
        util.logAmplitudeEvent("Error Retrieving Prices")
      }
    }

    /**
     * Get availability for spaces on a given date
     * Uses a memoized call to API endpoint 'get-availability' to check availability
     * @param {string} date
     * @returns object availObj
     */
    async function getAvailabilityData(date) {
      try {
        if (spaceAvailabilityMap.hasOwnProperty(date)) {
          return spaceAvailabilityMap[date]
        }
        const dateAvail = await util.api
          .get(`get-availability-combined?date=${date}`, {
            retry: { limit: 4 },
          })
          .json()
        spaceAvailabilityMap[date] = dateAvail.availability
        return dateAvail.availability
      } catch (err) {
        util.logToSentry(err)
        util.logAmplitudeEvent("Error Retrieving Availability")
      }
    }

    function selectSpaceAmplitude(e) {
      // Use currentTarget rather than target to get the <a> tag
      const eventProperties = {
        Slug: new URL(e.currentTarget.href).pathname.split("/").pop(),
        Space: e.currentTarget
          .querySelector(".space-name")
          .innerText.replace("'", ""),
        Venue: e.currentTarget
          .querySelector(".venue-name")
          .innerText.replace("'", ""),
        "Venue + Space": e.currentTarget
          .querySelector(".name-hidden")
          .innerText.replace("'", ""),
        Capacity: e.currentTarget.querySelector(".capacity-hidden").innerText,
        Price: e.currentTarget.querySelector(".price").innerText,
        "Selected From": "List",
      }
      const alternateTimes = getAlternateTimesData(e.currentTarget)
      eventProperties["Partial Availability"] =
        alternateTimes["Partial Availability"]
      eventProperties["Available Times"] = alternateTimes["Available Times"]
      try {
        let offerMessage =
          e.currentTarget.querySelector(".offer-pill").innerText
        if (offerMessage) {
          eventProperties["Offer Message"] = offerMessage
        }
      } catch (err) {
        util.logToSentry(err)
      }
      logAmplitudeEvent("Select Space", eventProperties)
    }

    function getAlternateTimesData(spaceElem) {
      if (!date || !endTime) {
        return {}
      }
      const result = {}

      try {
        const alternateTimesNote = spaceElem.querySelector(
          ".alternate-times .text-block-small"
        )
        if (alternateTimesNote) {
          if (spaceElem.querySelectorAll(".pill").length > 0) {
            result["Partial Availability"] = alternateTimesNote.innerText
            result["Available Times"] = spaceElem.querySelector(
              ".alternate-times .pill"
            ).innerText
          } else {
            result["Partial Availability"] = alternateTimesNote.innerText
          }
        }
      } catch (err) {
        util.logToSentry(err)
      }

      return result
    }

    function displayPopupBackgrounds() {
      for (const p of document.getElementsByClassName("popup-background")) {
        p.classList.add("dark")
        // Otherwise the sort container shows a white border
        document.getElementById("sort-container").style.zIndex = "auto"
        p.style.display = "block"
      }
    }

    const amplitudeLogSearch = util.debounce(async (searchFieldId) => {
      const searchTerm = document.getElementById(searchFieldId).value
      if (searchTerm.length >= 3) {
        logAmplitudeEvent("Search", {
          "Search Term": searchTerm,
          "Search Field": searchFieldId,
        })
      }
    }, 5000)

    const amplitudeLogLocationSearch = util.debounce(async () => {
      const searchTerm = document.getElementById("location-search").value
      if (searchTerm.length >= 3) {
        logAmplitudeEvent("Location Search", {
          "Search Term": searchTerm,
        })
      }
    }, 5000)

    function init() {
      try {
        document
          .getElementById("search-field-desktop")
          .addEventListener("input", function () {
            amplitudeLogSearch("search-field-desktop")
          })
      } catch (err) {
        util.logToSentry(err)
      }
      try {
        document
          .getElementById("search-field-mobile")
          .addEventListener("input", function () {
            amplitudeLogSearch("search-field-mobile")
          })
      } catch (err) {
        util.logToSentry(err)
      }
      try {
        document
          .getElementById("location-search")
          .addEventListener("input", function () {
            amplitudeLogLocationSearch()
          })
      } catch (err) {
        util.logToSentry(err)
      }

      try {
        defaultPageTitle = document.title
        defaultMetaDesc = document
          .querySelector('meta[name="description"]')
          .getAttribute("content")
        defaultH1 = document.getElementById("h1-title").innerText
        defaultMainH2 = document.getElementById("main-h2").innerText
        defaultMainH2Body = document.getElementById("main-h2-body").innerText
        defaultLocation = "London"
      } catch (err) {
        util.logToSentry(err)
      }

      // displayOfferBanner(searchParams);

      // Focus search field when icon clicked.
      try {
        document
          .getElementById("search-icon")
          .addEventListener("click", function () {
            document
              .getElementById("search-field-mobile")
              .classList.remove("hide")
            document.getElementById("search-field-mobile").focus()
            document.getElementById("search-icon").classList.add("hide")
          })
        document
          .getElementById("desktop-search-button")
          .addEventListener("click", function () {
            document
              .getElementById("search-field-desktop")
              .classList.remove("hide")
            document.getElementById("search-field-desktop").focus()
            document
              .getElementById("desktop-search-button")
              .classList.add("hide")
          })
      } catch (err) {
        util.logToSentry(err)
      }

      // If search query string has been provided then display the search box
      try {
        if (searchParams.has("search")) {
          document.getElementById("search-icon").click()
          document.getElementById("desktop-search-button").click()
          logAmplitudeEvent("Search Button Auto Clicked")
        }
      } catch (err) {
        util.logToSentry(err)
      }

      try {
        window.addEventListener("popstate", function (e) {
          // Check if there are any previous pushStates that we need to 'pop'.
          // If not do nothing, if there are check state and call appropriate
          // function to reverse the state, or just reload.
          if (util.popStateCheck()) {
            if (e.state) {
              if (e.state.type === "map") {
                selectShowMap(false)
              } else if (e.state.type === "list") {
                showListView(false)
              } else {
                window.location.href = location.href
              }
            } else {
              window.location.href = location.href
            }
          }
        })
      } catch (err) {
        util.logToSentry(err)
      }

      // Highlight date button when clicked
      try {
        document
          .getElementById("date-button")
          .addEventListener("click", function (e) {
            if (
              !document
                .getElementById("date-button")
                .classList.contains("highlighted")
            ) {
              closeModals(false, true, true)
              document
                .getElementById("date-button")
                .classList.add("highlighted")
              document
                .getElementById("date-button")
                .parentNode.classList.add("highlighted")
              document
                .getElementById("search-widget")
                .classList.add("highlighted")
              displayPopupBackgrounds()

              // On iPhone tapping Location opens the keyboard. If you tap Date straight after, the
              // keyboard closing activates scroll, so add slight delay.
              setTimeout(function () {
                window.addEventListener("scroll", closeCalendarOnScroll)
              }, 1200)
              logAmplitudeEvent("Open Choose Date")
            } else {
              closeModals()
            }
          })
      } catch (err) {
        util.logToSentry(err)
      }

      // Add listeners to the time modal buttons
      try {
        const timeButtons = document.getElementsByClassName("time-button")
        for (const tb of timeButtons) {
          tb.addEventListener("click", timeButtonClick)
        }
        let elem = document.getElementById("time-modal-clear")
        elem.addEventListener("click", timeClear)
        elem = document.getElementById("time-modal-close")
        elem.addEventListener("click", timeClose)
      } catch (err) {
        util.logToSentry(err)
      }

      // Display sort modal
      try {
        document
          .getElementById("sort-button")
          .addEventListener("click", function (e) {
            if (
              !document
                .getElementById("sort-button")
                .classList.contains("highlighted")
            ) {
              closeModals(true, false, true)
              document.getElementById("sort-modal").style.display = "flex"
              document
                .getElementById("sort-button")
                .classList.add("highlighted")
              for (const p of document.getElementsByClassName(
                "popup-background"
              )) {
                p.style.display = "block"
              }
              logAmplitudeEvent("Open Sort")
            } else {
              closeModals()
            }
          })
      } catch (err) {
        util.logToSentry(err)
      }

      // Search widget button - other buttons filter the spaces so just blur to
      // show the user something happened.
      try {
        document
          .getElementById("search-go")
          .addEventListener("click", function (e) {
            closeModals()
            blurSpaces(e.currentTarget)
            logAmplitudeEvent("Press Search Widget Button")
          })
      } catch (err) {
        util.logToSentry(err)
      }

      // Display location modal
      try {
        document
          .getElementById("location-button")
          .addEventListener("click", function (e) {
            if (
              !document
                .getElementById("location-button")
                .classList.contains("highlighted")
            ) {
              closeModals(true, true, false)
              document.getElementById("location-modal").style.display = "flex"
              document.getElementById("location-search").focus()
              document
                .getElementById("location-button")
                .classList.add("highlighted")
              document
                .getElementById("location-button")
                .parentNode.classList.add("highlighted")
              document
                .getElementById("search-widget")
                .classList.add("highlighted")
              displayPopupBackgrounds()
              logAmplitudeEvent("Open Location")
            } else {
              closeModals()
            }
          })
      } catch (err) {
        util.logToSentry(err)
      }

      // Near me button - ask browser for long+lat then process spaces
      try {
        document
          .getElementById("near-me")
          .addEventListener("click", function () {
            logAmplitudeEvent("Select Near Me")
            selectNearMe()
          })
      } catch (err) {
        util.logToSentry(err)
      }
      try {
        document
          .getElementById("map-column-near-me")
          .addEventListener("click", function () {
            logAmplitudeEvent("Select Map Near Me")
            selectNearMe()
          })
      } catch (err) {
        util.logToSentry(err)
      }

      // Areas
      try {
        const areas = document
          .getElementById("collection-list-area")
          .getElementsByClassName("icon-and-text-block-area")
        for (const area of areas) {
          area.addEventListener("click", async function (e) {
            let slug = e.currentTarget.querySelector(".slug-hidden").innerText
            util.updateUrlParam("area", slug, false, false)
            util.deleteUrlParam("loc")
            setAreaFromButton(e.currentTarget, false, true)
            scrollToTopIfNeeded()
            autoOpenCalendar(1000)
            logAmplitudeEvent("Select Area")
          })
        }
      } catch (err) {
        util.logToSentry(err)
      }

      // Remove filters button - just zoom map out to all London
      try {
        document
          .getElementById("remove-filters-button")
          .addEventListener("click", function () {
            util.updateUrlParam("area", "london", false, false)
            util.deleteUrlParam("loc")
            setArea("london", false)
            logAmplitudeEvent("View Hidden Spaces")
          })
      } catch (err) {
        util.logToSentry(err)
      }

      // Floating Map and List buttons
      try {
        document
          .getElementById("floating-map-button")
          .addEventListener("click", function (e) {
            selectShowMap()
            logAmplitudeEvent("Show Map")
          })
        document
          .getElementById("floating-list-button")
          .addEventListener("click", function (e) {
            showListView()
            logAmplitudeEvent("Show List")
          })
        document
          .getElementById("map-column-close")
          .addEventListener("click", function (e) {
            showListView()
            logAmplitudeEvent("Close Map View")
          })
      } catch (err) {
        util.logToSentry(err)
      }

      // Location help popup
      try {
        const locationHelpButton = document.getElementById(
          "location-help-button"
        )
        locationHelpButton.addEventListener("click", function (e) {
          document.getElementById("location-help").style.display = "none"
          logAmplitudeEvent("Accept Location Filter Warning")
        })
      } catch (err) {
        util.logToSentry(err)
      }

      // Close modals when click outside
      try {
        for (const p of document.getElementsByClassName("popup-background")) {
          p.addEventListener("click", (e) => {
            if (e.target.classList.contains("popup-background")) {
              if (timeStatus === TIME_START) {
                timeClear()
              }
              closeModals()
            }
          })
        }
      } catch (err) {
        util.logToSentry(err)
      }

      // Amplitude Select Space events
      try {
        const spaceCards = document.getElementsByClassName("space-card")
        for (const sc of spaceCards) {
          sc.addEventListener("click", selectSpaceAmplitude)
        }
      } catch (err) {
        util.logToSentry(err)
      }

      // Add event listeners to each sort button.
      try {
        document
          .getElementById("sort-order-high-to-low")
          .addEventListener("click", function (e) {
            sort("sort-order-high-to-low")
            logAmplitudeEvent("Sort", {
              "Sort By": e.currentTarget.dataset.displayText,
            })
          })
        document
          .getElementById("price-low-to-high")
          .addEventListener("click", function (e) {
            sort("price-low-to-high")
            logAmplitudeEvent("Sort", {
              "Sort By": e.currentTarget.dataset.displayText,
            })
          })
        document
          .getElementById("price-high-to-low")
          .addEventListener("click", function (e) {
            sort("price-high-to-low")
            logAmplitudeEvent("Sort", {
              "Sort By": e.currentTarget.dataset.displayText,
            })
          })
        document
          .getElementById("capacity-low-to-high")
          .addEventListener("click", function (e) {
            sort("capacity-low-to-high")
            logAmplitudeEvent("Sort", {
              "Sort By": e.currentTarget.dataset.displayText,
            })
          })
        document
          .getElementById("capacity-high-to-low")
          .addEventListener("click", function (e) {
            sort("capacity-high-to-low")
            logAmplitudeEvent("Sort", {
              "Sort By": e.currentTarget.dataset.displayText,
            })
          })
        document
          .getElementById("distance-low-to-high")
          .addEventListener("click", function (e) {
            sort("distance-low-to-high")
            logAmplitudeEvent("Sort", {
              "Sort By": e.currentTarget.dataset.displayText,
            })
          })
      } catch (err) {
        util.logToSentry(err)
      }

      // Amplitude Select Type event on type links.
      try {
        const typeButtons = document.getElementsByClassName("type-button")
        for (const b of typeButtons) {
          // Add click event listener
          b.addEventListener("click", function (e) {
            scrollToTopIfNeeded()
            // Fire Amplitude event
            const eventProperties = {
              Type: e.target.innerText.replace("'", ""),
            }
            try {
              logAmplitudeEvent("Select Type", eventProperties)
            } catch (err) {
              util.logToSentry(err)
            }
          })
        }
      } catch (err) {
        util.logToSentry(err)
      }

      try {
        document
          .getElementById("show-more")
          .addEventListener("click", function (e) {
            refreshRemoveFilters()
          })
      } catch (err) {
        util.logToSentry(err)
      }

      // Flatpickr calendar
      try {
        let monthsFromNow = new Date()
        monthsFromNow.setDate(monthsFromNow.getDate() + 365)
        let fourDaysFromNow = new Date()
        fourDaysFromNow.setDate(fourDaysFromNow.getDate() + 4)

        fp = flatpickr(".flatpickr", {
          enableTime: false,
          wrap: true,
          minDate: fourDaysFromNow,
          maxDate: monthsFromNow,
          disableMobile: "true",
          monthSelectorType: "static",
          /* If the calendar auto opens we don't want it to close when the user 
            interacts with the cookie consent modal or scrolls on mobile  */
          ignoredFocusElements: [window.document.body],
          locale: {
            firstDayOfWeek: 1, // start week on Monday
          },
          onChange: function (selectedDates, dateStr, instance) {
            flatpickrDateChange(selectedDates)
          },
        })
      } catch (err) {
        util.logToSentry(err)
      }

      try {
        // clear dates button
        const clearDates = document.getElementById("clear-dates")
        clearDates.addEventListener("click", (e) => {
          fp.clear()
          date = null
          util.TIME_PARAMS.forEach((param) => {
            util.deleteUrlParam(param)
          })
          timeClear()
          document.getElementById("date-text").innerText = "Date"
          document.getElementById("time-text").innerText = ""
          logAmplitudeEvent("Clear Dates")
          clearDates.style.visibility = "hidden"
          e.stopPropagation()
          e.preventDefault() // https://stackoverflow.com/questions/20215248/prevent-href-link-from-changing-the-url-hash
        })
      } catch (err) {
        util.logToSentry(err)
      }

      /************************
       * Initialise A/B Tests *
       ************************/
      // Run a dummy or calibration or A/A test
      try {
        util.initAbTest("aaTest", "A/A Test", "aatest")
      } catch (err) {
        util.logToSentry(err)
      }

      /**
       * Load asynchronous data and resolve promise once done
       * Called synchronously to not block thread and avoid async/await chain
       */
      try {
        initialAvailabilityData()
      } catch (err) {
        util.logToSentry(err)
      }

      // Populate the typeItems array with data from the Webflow types list page (long term replace with API calls)
      try {
        populateTypeItems()
      } catch (err) {
        util.logToSentry(err)
      }

      // Without this, if you select a date then switch away from the browser, when you click back on the page Flatpickr opens again.
      try {
        document.getElementById("date-input").addEventListener(
          "focus",
          function (e) {
            e.currentTarget.blur()
          },
          true
        )
        document.getElementById("date-button").addEventListener(
          "focus",
          function (e) {
            e.currentTarget.blur()
          },
          true
        )
      } catch (err) {
        util.logToSentry(err)
      }

      try {
        if (!searchParams.has("loc")) {
          processAreaFromUrl()
        }
      } catch (err) {
        util.logToSentry(err)
      }

      try {
        // Prevent form submission if the user presses Enter within the input
        document
          .getElementById("search-field-desktop")
          .addEventListener("keydown", function (event) {
            if (event.code === "Enter") {
              event.preventDefault()
            }
          })
        document
          .getElementById("search-field-mobile")
          .addEventListener("keydown", function (event) {
            if (event.code === "Enter") {
              event.preventDefault()
            }
          })
      } catch (err) {
        logAmplitudeEvent(err)
      }

      try {
        const loader = new Loader({
          apiKey: util.GMAP_PUBLISHABLE_KEY,
          version: "weekly",
          libraries: ["geometry", "places"],
          region: "GB",
        })
        loader.load().then(gmapMainRun)
      } catch (err) {
        util.logToSentry(err)
      }
    }

    return {
      init: init,
      jetboostFilterComplete: jetboostFilterComplete,
      jetboostPaginationComplete: jetboostPaginationComplete,
      logAmplitudeEvent: logAmplitudeEvent,
    }
  })()
}
